import React, { useEffect, useMemo, useState } from 'react';
import { ApiError, apiFetch } from '../api';
import { formatDateTime } from '../date';
import { Modal } from '../components/Modal';
import { useToast } from '../toast';
import { useMessageToast } from '../useMessageToast';
import { useDialog } from '../dialog';

type ShareItem = {
  id: string;
  toolId: string;
  createdAt: number;
  expiresAt: number | null;
  revokedAt: number | null;
  shareUrl: string;
};

type ShareDetail = ShareItem & {
  snapshotHtml: string;
  snapshotJson: string;
};

type ShareDetailResp = { ok: boolean } & ShareDetail;

function enhanceSnapshotHtmlForPreview(html: string) {
  const style = `<style>
  :root { color-scheme: dark; }
  html, body { margin: 0; padding: 0; background: #0b1020; color: rgba(255,255,255,0.92);
    font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, 'Microsoft YaHei', sans-serif; }
  a { color: inherit; }
  body { padding: 14px; }
  h1, h2, h3 { margin: 14px 0 8px; font-weight: 900; }
  hr { border: 0; border-top: 1px solid rgba(255,255,255,0.14); margin: 14px 0; }
  pre { white-space: pre-wrap; line-height: 1.75; padding: 12px 14px; border-radius: 14px;
    border: 1px solid rgba(255,255,255,0.12); background: rgba(255,255,255,0.06);
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace; }
  code { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace; }
  </style>`;

  const s = String(html || '');
  if (!s.trim()) return `<!doctype html><html><head>${style}</head><body></body></html>`;
  if (/<head[\s>]/i.test(s)) return s.replace(/<head[\s>][^>]*>/i, (m) => `${m}\n${style}\n`);
  if (/<html[\s>]/i.test(s)) return s.replace(/<html[\s>][^>]*>/i, (m) => `${m}\n<head>${style}</head>\n`);
  return `<!doctype html><html><head>${style}</head><body>${s}</body></html>`;
}

function JsonTree({
  value,
  name,
  depth
}: {
  value: any;
  name?: string;
  depth: number;
}) {
  const label = name ? <span style={{ color: 'rgba(255,255,255,0.88)', fontWeight: 900 }}>{name}</span> : null;

  if (value === null) {
    return (
      <div>
        {label ? <span>{label}: </span> : null}
        <span style={{ color: 'rgba(255,255,255,0.70)' }}>null</span>
      </div>
    );
  }
  if (typeof value === 'string') {
    return (
      <div>
        {label ? <span>{label}: </span> : null}
        <span style={{ color: '#a78bfa' }}>{JSON.stringify(value)}</span>
      </div>
    );
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return (
      <div>
        {label ? <span>{label}: </span> : null}
        <span style={{ color: '#22c55e' }}>{String(value)}</span>
      </div>
    );
  }

  const isArr = Array.isArray(value);
  const keys = isArr ? value.map((_: any, i: number) => i) : Object.keys(value || {});
  const open = depth < 1 || keys.length <= 6;
  const summaryText = isArr ? `Array(${keys.length})` : `Object(${keys.length})`;

  return (
    <details
      open={open}
      style={{
        border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: 14,
        padding: '10px 12px',
        background: 'rgba(255,255,255,0.06)'
      }}
    >
      <summary style={{ cursor: 'pointer', fontWeight: 900, color: 'rgba(255,255,255,0.9)' }}>
        {label ? <span>{label} · </span> : null}
        <span style={{ color: 'rgba(255,255,255,0.7)' }}>{summaryText}</span>
      </summary>
      <div style={{ display: 'grid', gap: 8, marginTop: 10, paddingLeft: 4 }}>
        {isArr
          ? value.map((v: any, i: number) => <JsonTree key={`${depth}-${i}`} name={String(i)} value={v} depth={depth + 1} />)
          : Object.entries(value || {}).map(([k, v]) => <JsonTree key={`${depth}-${k}`} name={k} value={v} depth={depth + 1} />)}
      </div>
    </details>
  );
}

function fmt(ts: number | null) {
  if (!ts) return '-';
  return formatDateTime(ts) || new Date(ts).toLocaleString();
}

function sanitizeAdminHtml(input: string) {
  return String(input || '')
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
    .replace(/\son\w+=(['"]).*?\1/gi, '')
    .replace(/\son\w+=([^\s>]+)/gi, '')
    .replace(/javascript:/gi, '');
}

function fmtOnline(seconds: number | null | undefined) {
  const sec = Math.max(0, Math.floor(Number(seconds || 0)));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${h}时${String(m).padStart(2, '0')}分${String(s).padStart(2, '0')}秒`;
  return `${m}分${String(s).padStart(2, '0')}秒`;
}

export function SharesPanel() {
  const { prompt, confirmDelete, confirmReset, confirmRevoke } = useDialog();
  const [items, setItems] = useState<ShareItem[]>([]);
  const [msg, setMsg] = useState<string | null>(null);
  const [detail, setDetail] = useState<ShareDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTab, setDetailTab] = useState<'preview' | 'json'>('preview');
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState<'REVOKE' | 'DELETE'>('REVOKE');
  const [bulkConfirm, setBulkConfirm] = useState('');
  const [bulkRunning, setBulkRunning] = useState(false);
  useMessageToast(msg, setMsg, 'profile-panels-shares');

  const load = async () => {
    setMsg(null);
    const data = await apiFetch<{ ok: boolean; items: ShareItem[] }>('/api/shares', { method: 'GET' });
    setItems(data.items || []);
    setSelected({});
  };

  const copyText = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setMsg('已复制');
    } catch {
      try {
        await prompt({ title: '复制失败', message: '复制失败，请手动复制：', defaultValue: text, readOnly: true, showCancel: false, confirmText: '关闭' });
      } catch {
      }
      setMsg('复制失败，请手动复制');
    }
  };

  useEffect(() => {
    load().catch(() => setItems([]));
  }, []);

  const selectedIds = Object.entries(selected)
    .filter(([, v]) => v)
    .map(([k]) => k);
  const selectedCount = selectedIds.length;
  const needConfirm = bulkAction === 'DELETE' ? 'DELETE' : 'CONFIRM';
  const confirmOk = bulkConfirm.trim().toUpperCase() === needConfirm;

  return (
    <div>
      <div className="section-title">
        <h3>分享管理</h3>
        <span className="muted">撤销后链接立即失效；删除会移除分享记录</span>
      </div>
      <div className="row" style={{ marginBottom: 12, justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
          <button
            className="btn secondary"
            type="button"
            disabled={!items.length}
            onClick={() => {
              const next: Record<string, boolean> = {};
              const allSelected = items.every((it) => selected[it.id]);
              for (const it of items) next[it.id] = !allSelected;
              setSelected(next);
            }}
          >
            {items.length && items.every((it) => selected[it.id]) ? '取消全选' : '全选'}
          </button>
          <button className="btn secondary" disabled={!selectedCount} onClick={() => setBulkOpen(true)} type="button">
            批量操作（{selectedCount}）
          </button>
        </div>
        <button className="btn secondary" onClick={() => load().catch(() => setMsg('刷新失败'))}>
          刷新
        </button>
      </div>

      <div className="tool-grid">
        {items.map((it) => {
          const expired = !!it.expiresAt && Date.now() > it.expiresAt;
          const revoked = !!it.revokedAt;
          const status = revoked ? '已撤销' : expired ? '已过期' : '有效';
          return (
            <div key={it.id} className="tool">
              <div className="tool-top">
                <div className="row" style={{ gap: 10, alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    checked={!!selected[it.id]}
                    onChange={(e) => setSelected((s) => ({ ...s, [it.id]: e.target.checked }))}
                    style={{ width: 16, height: 16 }}
                  />
                  <div className="tool-name">{it.toolId}</div>
                </div>
                <span className="badge">{status}</span>
              </div>
              <div className="muted">创建：{fmt(it.createdAt)}</div>
              <div className="muted">过期：{fmt(it.expiresAt)}</div>
              <div className="row" style={{ justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                <div className="row" style={{ flexWrap: 'wrap' }}>
                  <button
                    className="btn secondary"
                    type="button"
                    onClick={async () => {
                      setMsg(null);
                      setDetailLoading(true);
                      try {
                        const d = await apiFetch<ShareDetailResp>(`/api/shares/${encodeURIComponent(it.id)}`, { method: 'GET' });
                        const { ok: _ok, ...rest } = d;
                        setDetail(rest);
                        setDetailTab('preview');
                      } catch {
                        setMsg('查看失败，请稍后重试');
                      } finally {
                        setDetailLoading(false);
                      }
                    }}
                  >
                    {detailLoading ? '加载中…' : '查看内容'}
                  </button>
                  <button className="btn secondary" type="button" onClick={() => copyText(it.shareUrl)}>
                    复制链接
                  </button>
                  <a className="btn secondary" href={it.shareUrl} target="_blank" rel="noreferrer">
                    打开链接
                  </a>
                </div>
                <div className="row" style={{ gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <button
                    className="btn danger"
                    type="button"
                    disabled={revoked}
                    onClick={async () => {
                      const ok = await confirmRevoke({ title: '撤销分享链接', message: '确认撤销该分享链接？撤销后链接立即失效' });
                      if (!ok) return;
                      try {
                        await apiFetch(`/api/shares/${encodeURIComponent(it.id)}/revoke`, { method: 'POST', body: JSON.stringify({}) });
                        await load();
                      } catch {
                        setMsg('撤销失败，请稍后重试');
                      }
                    }}
                  >
                    撤销
                  </button>
                  <button
                    className="btn secondary"
                    type="button"
                    onClick={async () => {
                      const ok = await confirmDelete({ title: '删除分享记录', message: '确认删除该分享记录？删除后无法恢复', confirmText: '确认删除' });
                      if (!ok) return;
                      try {
                        await apiFetch(`/api/shares/${encodeURIComponent(it.id)}`, { method: 'DELETE' });
                        await load();
                      } catch {
                        setMsg('删除失败，请稍后重试');
                      }
                    }}
                  >
                    删除
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {!items.length ? <div className="muted">暂无分享记录</div> : null}

      <Modal open={!!detail} title={detail ? `${detail.toolId} · 分享内容` : undefined} onClose={() => setDetail(null)}>
        {detail ? (
          <div style={{ display: 'grid', gap: 12 }}>
            <div className="row" style={{ justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
              <div className="muted">
                创建：{fmt(detail.createdAt)}｜过期：{fmt(detail.expiresAt)}｜撤销：{fmt(detail.revokedAt)}
              </div>
              <div className="row" style={{ flexWrap: 'wrap' }}>
                <button className="btn secondary" onClick={() => copyText(detail.shareUrl)}>
                  复制链接
                </button>
                <a className="btn secondary" href={detail.shareUrl} target="_blank" rel="noreferrer">
                  打开链接
                </a>
              </div>
            </div>

            <div className="row" style={{ gap: 8 }}>
              <button
                className="btn secondary"
                style={detailTab === 'preview' ? { borderColor: 'rgba(139, 92, 246, 0.55)', background: 'rgba(139, 92, 246, 0.16)' } : undefined}
                onClick={() => setDetailTab('preview')}
              >
                预览
              </button>
              <button
                className="btn secondary"
                style={detailTab === 'json' ? { borderColor: 'rgba(139, 92, 246, 0.55)', background: 'rgba(139, 92, 246, 0.16)' } : undefined}
                onClick={() => setDetailTab('json')}
              >
                JSON
              </button>
            </div>

            {detailTab === 'preview' ? (
              <div className="tool">
                <div className="tool-top">
                  <div className="tool-name">内容预览</div>
                  <span className="badge">只读</span>
                </div>
                <iframe
                  title="share-preview"
                  srcDoc={enhanceSnapshotHtmlForPreview(detail.snapshotHtml)}
                  style={{ width: '100%', height: 520, border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14, background: 'rgba(255,255,255,0.04)' }}
                />
              </div>
            ) : (
              <div className="tool">
                <div className="tool-top">
                  <div className="tool-name">Snapshot JSON</div>
                  <span className="badge">只读</span>
                </div>
                <div className="row" style={{ justifyContent: 'flex-end' }}>
                  <button className="btn secondary" onClick={() => copyText(detail.snapshotJson)}>
                    复制 JSON
                  </button>
                </div>
                <div style={{ marginTop: 10 }}>
                  {(() => {
                    try {
                      const obj = JSON.parse(detail.snapshotJson || '{}');
                      return <JsonTree value={obj} depth={0} />;
                    } catch {
                      return <pre>{detail.snapshotJson}</pre>;
                    }
                  })()}
                </div>
              </div>
            )}
          </div>
        ) : null}
      </Modal>

      <Modal open={cfgOpen} onClose={() => setCfgOpen(false)} title="用户配置">
        <div className="muted" style={{ lineHeight: 1.8 }}>
          目标账号：{cfgUser?.email || '-'}
        </div>
        {cfgLoading ? (
          <div className="muted" style={{ marginTop: 10 }}>
            加载中…
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 12, marginTop: 12 }}>
            <div className="tool">
              <div className="tool-top">
                <div className="tool-name">AI 配置</div>
                <span className="badge">{userAiCfg ? '已配置' : '未配置'}</span>
              </div>
              <div className="muted" style={{ lineHeight: 1.8, marginTop: 10 }}>
                {userAiCfg ? (
                  <>
                    provider：{userAiCfg.provider}
                    <br />
                    baseUrl：{userAiCfg.baseUrl}
                    <br />
                    model：{userAiCfg.model}
                    <br />
                    enabled：{String(Boolean(userAiCfg.enabled))}
                    <br />
                    hasApiKey：{String(Boolean(userAiCfg.hasApiKey))}
                    <br />
                    updated：{userAiCfg.updatedAt ? fmt(Number(userAiCfg.updatedAt)) : '-'}
                  </>
                ) : (
                  '无'
                )}
              </div>
              <div className="row" style={{ marginTop: 12, justifyContent: 'flex-end' }}>
                <button
                  className="btn danger"
                  type="button"
                  disabled={!cfgUser}
                  onClick={async () => {
                    if (!cfgUser) return;
                    const ok = await confirmReset({
                      title: '重置 AI 配置',
                      message: `确认重置该用户 AI 配置？\n${cfgUser.email}\n重置后该用户需要重新保存 AI Key。`,
                      confirmText: '确认重置'
                    });
                    if (!ok) return;
                    setMsg(null);
                    try {
                      await apiFetch(`/api/admin/users/${encodeURIComponent(cfgUser.id)}/ai-config`, { method: 'DELETE' });
                      setMsg('已重置 AI 配置');
                      setUserAiCfg(null);
                    } catch {
                      setMsg('重置失败，请稍后重试');
                    }
                  }}
                >
                  重置
                </button>
              </div>
            </div>

            <div className="tool">
              <div className="tool-top">
                <div className="tool-name">Coze 配置</div>
                <span className="badge">{userCozeCfg ? '已配置' : '未配置'}</span>
              </div>
              <div className="muted" style={{ lineHeight: 1.8, marginTop: 10 }}>
                {userCozeCfg ? (
                  <>
                    botGeneral：{String(userCozeCfg.botGeneral || '') || '-'}
                    <br />
                    botMbti：{String(userCozeCfg.botMbti || '') || '-'}
                    <br />
                    botTarot：{String(userCozeCfg.botTarot || '') || '-'}
                    <br />
                    updated：{userCozeCfg.updatedAt ? fmt(Number(userCozeCfg.updatedAt)) : '-'}
                  </>
                ) : (
                  '无'
                )}
              </div>
              <div className="row" style={{ marginTop: 12, justifyContent: 'flex-end' }}>
                <button
                  className="btn danger"
                  type="button"
                  disabled={!cfgUser}
                  onClick={async () => {
                    if (!cfgUser) return;
                    const ok = await confirmReset({
                      title: '重置 Coze 配置',
                      message: `确认重置该用户 Coze 配置？\n${cfgUser.email}`,
                      confirmText: '确认重置'
                    });
                    if (!ok) return;
                    setMsg(null);
                    try {
                      await apiFetch(`/api/admin/users/${encodeURIComponent(cfgUser.id)}/coze-config`, { method: 'DELETE' });
                      setMsg('已重置 Coze 配置');
                      setUserCozeCfg(null);
                    } catch {
                      setMsg('重置失败，请稍后重试');
                    }
                  }}
                >
                  重置
                </button>
              </div>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={bulkOpen} onClose={() => setBulkOpen(false)} title="批量操作">
        <div className="muted" style={{ lineHeight: 1.8 }}>
          已选择 {selectedCount} 个用户。
          <br />
          批量操作比单条操作更高风险，请谨慎。
        </div>
        <div className="field" style={{ marginTop: 12 }}>
          <div className="muted">操作</div>
          <select value={bulkAction} onChange={(e) => setBulkAction(e.target.value as any)}>
            <option value="CANCEL">批量注销（保留数据）</option>
            <option value="RESTORE">批量恢复（可登录）</option>
            <option value="UNLOCK_LOGIN">批量解锁登录失败</option>
            <option value="DELETE">批量删除（不可恢复）</option>
          </select>
        </div>
        <div className="muted" style={{ marginTop: 10, lineHeight: 1.8 }}>
          {bulkAction === 'DELETE'
            ? '删除为物理删除，且会级联清理该用户的历史/导出/分享等数据，无法恢复。'
            : bulkAction === 'CANCEL'
              ? '注销后用户不可登录，但数据仍保留。'
              : bulkAction === 'RESTORE'
                ? '恢复后用户可再次登录。'
                : '解锁会清空该用户邮箱的登录失败计数。'}
        </div>
        <div className="field" style={{ marginTop: 12 }}>
          <div className="muted">确认输入：{needConfirm}</div>
          <input value={bulkConfirm} onChange={(e) => setBulkConfirm(e.target.value.slice(0, 20))} placeholder={needConfirm} />
        </div>
        <div className="muted" style={{ marginTop: 10, lineHeight: 1.8 }}>
          目标邮箱（前 10 个）：
          <br />
          {items
            .filter((u) => selectedIds.includes(u.id))
            .slice(0, 10)
            .map((u) => u.email)
            .join('、') || '-'}
          {selectedCount > 10 ? <span> …（共 {selectedCount} 个）</span> : null}
        </div>
        <div className="row" style={{ marginTop: 12, justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
          <button
            className="btn danger"
            disabled={bulkRunning || !selectedCount || !confirmOk}
            onClick={async () => {
              const ids = selectedIds;
              if (!ids.length) return;
              setBulkRunning(true);
              setMsg(null);
              try {
                const r = await apiFetch<{ ok: boolean; items: Array<{ id: string; ok: boolean; error?: string }> }>('/api/admin/users/bulk', {
                  method: 'POST',
                  body: JSON.stringify({ action: bulkAction, userIds: ids })
                });
                const okN = (r.items || []).filter((x) => x.ok).length;
                const fail = (r.items || []).filter((x) => !x.ok);
                setMsg(fail.length ? `批量完成：成功 ${okN}，失败 ${fail.length}` : `批量完成：成功 ${okN}`);
                setBulkOpen(false);
                setBulkConfirm('');
                setSelected({});
                await loadUsers();
              } catch {
                setMsg('批量执行失败，请稍后重试');
              } finally {
                setBulkRunning(false);
              }
            }}
            type="button"
          >
            {bulkRunning ? '执行中…' : '执行'}
          </button>
          <button className="btn secondary" type="button" onClick={() => setBulkOpen(false)}>
            取消
          </button>
        </div>
      </Modal>

      <Modal open={bulkOpen} title="批量操作" onClose={() => setBulkOpen(false)}>
        <div className="muted" style={{ lineHeight: 1.8 }}>
          已选择 {selectedCount} 条分享记录。
        </div>
        <div className="field" style={{ marginTop: 12 }}>
          <div className="muted">操作</div>
          <select value={bulkAction} onChange={(e) => setBulkAction(e.target.value as any)}>
            <option value="REVOKE">批量撤销（链接失效，保留记录）</option>
            <option value="DELETE">批量删除（移除记录，不可恢复）</option>
          </select>
        </div>
        <div className="field" style={{ marginTop: 12 }}>
          <div className="muted">确认输入：{needConfirm}</div>
          <input value={bulkConfirm} onChange={(e) => setBulkConfirm(e.target.value.slice(0, 20))} placeholder={needConfirm} />
        </div>
        <div className="row" style={{ marginTop: 12, justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
          <button
            className={bulkAction === 'DELETE' ? 'btn danger' : 'btn'}
            type="button"
            disabled={bulkRunning || !selectedCount || !confirmOk}
            onClick={async () => {
              const ids = selectedIds;
              if (!ids.length) return;
              setBulkRunning(true);
              setMsg(null);
              try {
                const url = bulkAction === 'DELETE' ? '/api/shares/bulk-delete' : '/api/shares/bulk-revoke';
                const r = await apiFetch<{ ok: boolean; items: Array<{ id: string; ok: boolean }> }>(url, { method: 'POST', body: JSON.stringify({ ids }) });
                const okN = (r.items || []).filter((x) => x.ok).length;
                const failN = (r.items || []).length - okN;
                setMsg(failN ? `批量完成：成功 ${okN}，失败 ${failN}` : `批量完成：成功 ${okN}`);
                setBulkOpen(false);
                setBulkConfirm('');
                await load();
              } catch {
                setMsg('批量执行失败，请稍后重试');
              } finally {
                setBulkRunning(false);
              }
            }}
          >
            {bulkRunning ? '执行中…' : '执行'}
          </button>
          <button className="btn secondary" type="button" onClick={() => setBulkOpen(false)}>
            取消
          </button>
        </div>
      </Modal>
    </div>
  );
}

type AiConfig = {
  provider: string;
  baseUrl: string;
  model: string;
  apiType: 'chat' | 'responses';
  enabled: boolean;
  maxTokens: number;
  temperature: number;
  promptStrictness: number;
  hasApiKey: boolean;
};

type CozeConfig = {
  botGeneral: string;
  botMbti: string;
  botTarot: string;
};

type AdminUser = {
  id: string;
  email: string;
  createdAt: number;
  nickname?: string | null;
  status?: number;
  canceledAt?: number | null;
  nicknameUpdatedAt?: number | null;
  onlineTotalSeconds?: number;
  lastActiveAt?: number | null;
};

type LoginFailureEvent = {
  id: string;
  email: string;
  reason: string;
  ip: string | null;
  userAgent: string | null;
  createdAt: number;
};

type LoginFailureState = {
  email: string;
  count: number;
  lockedUntil: number | null;
  updatedAt: number;
};

type AdminResultItem = {
  id: string;
  toolId: string;
  toolName: string;
  renderText: string;
  createdAt: number;
};

type AdminExportItem = {
  id: string;
  toolId: string;
  toolName: string;
  resultId: string;
  createdAt: number;
};

type AdminShareItem = {
  id: string;
  toolId: string;
  createdAt: number;
  expiresAt: number | null;
  revokedAt: number | null;
};

type AdminAuditLogItem = {
  id: string;
  actorUserId: string;
  actorEmail: string;
  action: string;
  targetUserId: string | null;
  targetEmail: string | null;
  meta: any;
  ip: string | null;
  userAgent: string | null;
  createdAt: number;
};

type SystemConfig = {
  publicBaseUrl: string;
  icpText: string;
  icpUrl: string;
  mpsText: string;
  mpsUrl: string;
  cookieSecure: string;
  aiRequestTimeoutMs: string;
  lunisolarServiceUrl: string;
  adminEmail: string;
};

type AdminRealtimeUser = {
  sessionId: string;
  userId: string;
  nickname: string;
  emailMasked: string;
  onlineSeconds: number;
  loginIp: string;
  browser: string;
  lastActionAt: number;
};

type AdminRealtimeTrendPoint = {
  at: number;
  count: number;
};

type AdminMaintenanceHistoryItem = {
  id: string;
  text: string;
  html: string;
  sentAt: number;
  actorEmail: string;
  successCount: number;
  failCount: number;
  filteredCount: number;
};

export function AiConfigPanel() {
  const [cfg, setCfg] = useState<AiConfig | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [cozeCfg, setCozeCfg] = useState<CozeConfig | null>(null);
  const [savingCoze, setSavingCoze] = useState(false);
  const [cozeMsg, setCozeMsg] = useState<string | null>(null);
  useMessageToast(msg, setMsg, 'profile-panels-ai');
  useMessageToast(cozeMsg, setCozeMsg, 'profile-panels-coze');

  useEffect(() => {
    apiFetch<{ ok: boolean; config: AiConfig }>('/api/user/ai-config', { method: 'GET' })
      .then((r) => setCfg(r.config))
      .catch(() => setCfg(null));
  }, []);

  useEffect(() => {
    apiFetch<{ ok: boolean; config: CozeConfig }>('/api/user/coze-config', { method: 'GET' })
      .then((r) => setCozeCfg(r.config))
      .catch(() => setCozeCfg({ botGeneral: '', botMbti: '', botTarot: '' }));
  }, []);

  const presetModels: Array<{ label: string; id: string; apiType: AiConfig['apiType'] }> = useMemo(
    () => [
      { label: '豆包 Pro 1.5（32k）', id: 'doubao-1-5-pro-32k-250115', apiType: 'chat' },
      { label: 'DeepSeek V3', id: 'deepseek-v3-250324', apiType: 'chat' },
      { label: 'DeepSeek R1', id: 'deepseek-r1-250528', apiType: 'chat' },
      { label: 'DeepSeek V3.1', id: 'deepseek-v3-1-250821', apiType: 'chat' },
      { label: '豆包 Seed 1.8', id: 'doubao-seed-1-8-251228', apiType: 'responses' },
      { label: 'GLM-4.7', id: 'glm-4-7-251222', apiType: 'responses' },
      { label: 'DeepSeek V3.2', id: 'deepseek-v3-2-251201', apiType: 'responses' },
      { label: 'Kimi K2 Thinking', id: 'kimi-k2-thinking-251104', apiType: 'responses' },
      { label: '豆包 Seed Code', id: 'doubao-seed-code-preview-251028', apiType: 'responses' },
      { label: '豆包 Seed 1.6 Lite', id: 'doubao-seed-1-6-lite-251015', apiType: 'responses' },
      { label: '豆包 Seed 1.6', id: 'doubao-seed-1-6-251015', apiType: 'responses' },
      { label: '豆包 Seed 1.6 Flash', id: 'doubao-seed-1-6-flash-250828', apiType: 'responses' },
      { label: '豆包 Seed 1.6 Vision', id: 'doubao-seed-1-6-vision-250815', apiType: 'responses' }
    ],
    []
  );

  const selectedPresetId = cfg && presetModels.some((m) => m.id === cfg.model) ? cfg.model : '';

  const parseModelDate = (modelId: string) => {
    const s = String(modelId || '').trim();
    const m = s.match(/(\d{6})$/);
    if (!m) return null;
    const raw = m[1];
    const yy = Number(raw.slice(0, 2));
    const mm = Number(raw.slice(2, 4));
    const dd = Number(raw.slice(4, 6));
    if (!Number.isFinite(yy) || !Number.isFinite(mm) || !Number.isFinite(dd)) return null;
    if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return null;
    const d = new Date(2000 + yy, mm - 1, dd);
    if (Number.isNaN(d.getTime())) return null;
    if (d.getFullYear() !== 2000 + yy || d.getMonth() !== mm - 1 || d.getDate() !== dd) return null;
    return d;
  };

  const isPreviewModel = (modelId: string) => /(?:^|[-_])preview(?:$|[-_])/i.test(String(modelId || ''));

  const isNewModel = (modelId: string) => {
    const d = parseModelDate(modelId);
    if (!d) return false;
    const days = 120;
    const cutoff = new Date(Date.now() - days * 24 * 3600 * 1000);
    return d.getTime() >= cutoff.getTime();
  };

  const clampIntFromInput = (raw: string, min: number, max: number, fallback: number) => {
    const digits = String(raw || '').replace(/[^\d]/g, '');
    const n = digits ? Number(digits) : Number.NaN;
    const v = Number.isFinite(n) ? Math.floor(n) : fallback;
    return Math.max(min, Math.min(max, v));
  };

  const clampFloatFromInput = (raw: string, min: number, max: number, fallback: number) => {
    const cleaned = String(raw || '').replace(/[^0-9.]/g, '');
    const m = cleaned.match(/^\d*(?:\.\d*)?/);
    const normalized = m ? m[0] : '';
    const n = normalized.length ? Number(normalized) : Number.NaN;
    const v = Number.isFinite(n) ? n : fallback;
    return Math.max(min, Math.min(max, v));
  };

  if (!cfg) {
    return <div className="muted">加载中…</div>;
  }

  return (
    <div>
      <div className="section-title">
        <h3>AI 配置</h3>
        <span className="muted">服务端加密保存，仅用于生成解读</span>
      </div>

      <div className="split">
        <div>
          <div className="field">
            <div className="muted">Base URL</div>
            <input value={cfg.baseUrl} onChange={(e) => setCfg({ ...cfg, baseUrl: e.target.value })} />
          </div>
          <div className="field" style={{ marginTop: 12 }}>
            <div className="muted">接口类型</div>
            <select value={cfg.apiType} onChange={(e) => setCfg({ ...cfg, apiType: e.target.value === 'responses' ? 'responses' : 'chat' })}>
              <option value="chat">Chat Completions（/chat/completions）</option>
              <option value="responses">Responses（/responses）</option>
            </select>
          </div>
          <div className="field" style={{ marginTop: 12 }}>
            <div className="muted">预设模型</div>
            <select
              value={selectedPresetId}
              onChange={(e) => {
                const id = e.target.value;
                const pick = presetModels.find((m) => m.id === id);
                if (!pick) return;
                setCfg({ ...cfg, model: pick.id, apiType: pick.apiType });
              }}
            >
              <option value="">选择一个常用模型（可选）</option>
              {presetModels.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                  {isPreviewModel(m.id) ? '（预览）' : ''}
                  {isNewModel(m.id) ? '（新）' : ''}
                  （{m.apiType}）
                </option>
              ))}
            </select>
          </div>
          <div className="field" style={{ marginTop: 12 }}>
            <div className="muted">Model</div>
            <input value={cfg.model} onChange={(e) => setCfg({ ...cfg, model: e.target.value })} />
            <div className="muted" style={{ marginTop: 8, lineHeight: 1.8 }}>
              - 这里填写模型 ID（例如 doubao-seed-1-8-251228）
              <br />- 不确定时优先用上面的“预设模型”，会自动匹配接口类型
            </div>
          </div>
          <div className="field" style={{ marginTop: 12 }}>
            <div className="muted">Max Tokens</div>
            <input
              value={String(cfg.maxTokens)}
              onChange={(e) => setCfg({ ...cfg, maxTokens: clampIntFromInput(e.target.value, 1, 32000, 2000) })}
              type="number"
              min={1}
              max={32000}
              step={1}
              inputMode="numeric"
            />
            <div className="muted" style={{ marginTop: 8, lineHeight: 1.8 }}>
              - 控制“最多输出多少 token”，越大越长，耗时/成本也更高
              <br />- 建议：800-2500（短解读）/ 2500-6000（更详细）
            </div>
          </div>
          <div className="field" style={{ marginTop: 12 }}>
            <div className="muted">Temperature</div>
            <input
              value={String(cfg.temperature)}
              onChange={(e) => setCfg({ ...cfg, temperature: clampFloatFromInput(e.target.value, 0, 2, 0.7) })}
              type="number"
              min={0}
              max={2}
              step={0.1}
              inputMode="decimal"
            />
            <div className="muted" style={{ marginTop: 8, lineHeight: 1.8 }}>
              - 控制输出“发散/随机程度”
              <br />- 越低越稳、越贴近给定数据（0.2-0.7）；越高越有创意但更可能跑题（0.8-1.2）
            </div>
          </div>
          <div className="field" style={{ marginTop: 12 }}>
            <div className="muted">输出约束</div>
            <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
              <input
                type="range"
                min={0}
                max={100}
                step={1}
                value={String(cfg.promptStrictness)}
                onChange={(e) => setCfg({ ...cfg, promptStrictness: clampIntFromInput(e.target.value, 0, 100, 50) })}
                style={{ flex: 1, minWidth: 220 }}
              />
              <input
                type="number"
                min={0}
                max={100}
                step={1}
                value={String(cfg.promptStrictness)}
                onChange={(e) => setCfg({ ...cfg, promptStrictness: clampIntFromInput(e.target.value, 0, 100, 50) })}
                inputMode="numeric"
                style={{ width: 90 }}
              />
            </div>
            <div className="muted" style={{ marginTop: 8, lineHeight: 1.8 }}>
              - 用于减少“过于死板只复述结果”的情况，同时控制幻觉
              <br />- 0 更放开（更多联想但不捏造事实），50 更平衡，100 更严格（更保守）
            </div>
          </div>
          <div className="row" style={{ marginTop: 12 }}>
            <label className="row" style={{ gap: 8 }}>
              <input type="checkbox" checked={cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} style={{ width: 18, height: 18 }} />
              <span className="muted">启用 AI 解读</span>
            </label>
            <span className="badge">{cfg.hasApiKey ? '已保存 Key' : '未保存 Key'}</span>
          </div>
          {!cfg.hasApiKey ? (
            <div className="muted" style={{ marginTop: 10, lineHeight: 1.8 }}>
              - 未保存个人 Key 时，将使用服务器默认 Key（默认 Key 无效会导致鉴权失败）
            </div>
          ) : null}
          <div className="muted" style={{ marginTop: 10, lineHeight: 1.8 }}>
            - Seed / Thinking 等模型通常需要选择 Responses 接口
            <br />- 如不确定可先用预设模型，或切换接口类型重试
          </div>
        </div>

        <div>
          <div className="field">
            <div className="muted">API Key（留空表示不修改）</div>
            <textarea value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="粘贴你的 Key" />
            <div className="muted" style={{ marginTop: 10, lineHeight: 1.8 }}>
              获取方式（火山方舟 / 豆包）：
              <br />
              1）登录火山引擎控制台 → 进入「火山方舟」→「系统管理」→「API Key 管理」→ 创建并复制 API Key。
              <br />
              2）建议按“资源项目（Project）”隔离权限，避免不同环境/团队共用同一 Key。
              <br />
              3）请勿公开 API Key；泄露可能导致用量被他人消耗。
              <div className="row" style={{ marginTop: 10, flexWrap: 'wrap', gap: 10 }}>
                <a
                  className="btn secondary"
                  href="https://console.volcengine.com/ark/region:ark+cn-beijing/apiKey"
                  target="_blank"
                  rel="noreferrer"
                >
                  打开 API Key 管理页
                </a>
                <a
                  className="btn secondary"
                  href="https://www.volcengine.com/docs/82379/1541594?lang=zh"
                  target="_blank"
                  rel="noreferrer"
                >
                  查看官方获取指引
                </a>
              </div>
            </div>
          </div>
          <div className="row" style={{ marginTop: 12 }}>
            <button
              className="btn"
              disabled={saving}
              onClick={async () => {
                setSaving(true);
                setMsg(null);
                try {
                  await apiFetch('/api/user/ai-config', { method: 'PUT', body: JSON.stringify({ ...cfg, apiKey: apiKey.trim() }) });
                  setApiKey('');
                  const fresh = await apiFetch<{ ok: boolean; config: AiConfig }>('/api/user/ai-config', { method: 'GET' });
                  setCfg(fresh.config);
                  setMsg('保存成功');
                } catch (e) {
                  if (e instanceof ApiError) {
                    const code = (e.body as any)?.error;
                    if (code === 'AI_CONFIG_INVALID') setMsg('保存失败：Base URL 不合法（请检查协议与端口）');
                    else if (code === 'ENCRYPTION_KEY_INVALID') setMsg('保存失败：服务端 ENCRYPTION_KEY_BASE64 配置错误（需 32 字节 base64）');
                    else if (code === 'API_KEY_REQUIRED') setMsg('保存失败：请填写 API Key');
                    else setMsg('保存失败，请稍后重试');
                  } else {
                    setMsg('保存失败');
                  }
                } finally {
                  setSaving(false);
                }
              }}
            >
              {saving ? '保存中…' : '保存'}
            </button>
          </div>
          <div className="muted" style={{ marginTop: 10, lineHeight: 1.8 }}>
            - 你保存的 Key 会在服务端使用 AES-256-GCM 加密存储
            <br />- 分享页禁用脚本执行，避免把 Key 暴露在前端
          </div>

          <div style={{ height: 14 }} />

          <div className="tool">
            <div className="tool-top">
              <div className="tool-name">智能体配置</div>
              <span className="badge">保存 Bot ID</span>
            </div>
            <div className="muted" style={{ lineHeight: 1.8 }}>
              用于“智能体”页面自动填充 Bot ID（仅保存 bot_id，不保存 Token）。
            </div>

            <div className="field" style={{ marginTop: 12 }}>
              <div className="muted">通用助手 Bot ID</div>
              <input value={cozeCfg?.botGeneral || ''} onChange={(e) => setCozeCfg({ ...(cozeCfg || { botGeneral: '', botMbti: '', botTarot: '' }), botGeneral: e.target.value })} />
            </div>
            <div className="field" style={{ marginTop: 12 }}>
              <div className="muted">MBTI 深度解读 Bot ID</div>
              <input value={cozeCfg?.botMbti || ''} onChange={(e) => setCozeCfg({ ...(cozeCfg || { botGeneral: '', botMbti: '', botTarot: '' }), botMbti: e.target.value })} />
            </div>
            <div className="field" style={{ marginTop: 12 }}>
              <div className="muted">塔罗解读助手 Bot ID</div>
              <input value={cozeCfg?.botTarot || ''} onChange={(e) => setCozeCfg({ ...(cozeCfg || { botGeneral: '', botMbti: '', botTarot: '' }), botTarot: e.target.value })} />
            </div>

            <div className="row" style={{ marginTop: 12, justifyContent: 'flex-end' }}>
              <button
                className="btn"
                disabled={savingCoze || !cozeCfg}
                onClick={async () => {
                  if (!cozeCfg) return;
                  setSavingCoze(true);
                  setCozeMsg(null);
                  try {
                    const r = await apiFetch<{ ok: boolean; config: CozeConfig }>('/api/user/coze-config', { method: 'PUT', body: JSON.stringify({ config: cozeCfg }) });
                    setCozeCfg(r.config);
                    setCozeMsg('保存成功');
                  } catch {
                    setCozeMsg('保存失败，请稍后重试');
                  } finally {
                    setSavingCoze(false);
                  }
                }}
                type="button"
              >
                {savingCoze ? '保存中…' : '保存'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function AdminPanel() {
  const { confirm, prompt, confirmDelete, confirmReset, confirmCancelAccount, confirmRevoke } = useDialog();
  const [adminTab, setAdminTab] = useState<'realtime' | 'users' | 'system' | 'logs' | 'stats'>('users');
  const [msg, setMsg] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [items, setItems] = useState<AdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState<'CANCEL' | 'RESTORE' | 'DELETE' | 'UNLOCK_LOGIN'>('CANCEL');
  const [bulkConfirm, setBulkConfirm] = useState('');
  const [bulkRunning, setBulkRunning] = useState(false);
  useMessageToast(msg, setMsg, 'profile-panels-admin');

  const [createEmail, setCreateEmail] = useState('');
  const [createPassword, setCreatePassword] = useState('');
  const [creating, setCreating] = useState(false);

  const [pwOpen, setPwOpen] = useState(false);
  const [pwUser, setPwUser] = useState<AdminUser | null>(null);
  const [pwNew, setPwNew] = useState('');
  const [pwSaving, setPwSaving] = useState(false);

  const [failEmail, setFailEmail] = useState('');
  const [failState, setFailState] = useState<LoginFailureState | null>(null);
  const [failStateLoading, setFailStateLoading] = useState(false);
  const [failItems, setFailItems] = useState<LoginFailureEvent[]>([]);
  const [failTotal, setFailTotal] = useState(0);
  const [failLoading, setFailLoading] = useState(false);
  const [failPage, setFailPage] = useState(1);
  const failPageSize = 50;

  const [editOpen, setEditOpen] = useState(false);
  const [editUser, setEditUser] = useState<AdminUser | null>(null);
  const [editEmail, setEditEmail] = useState('');
  const [editNickname, setEditNickname] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  const [dataOpen, setDataOpen] = useState(false);
  const [dataUser, setDataUser] = useState<AdminUser | null>(null);
  const [dataTab, setDataTab] = useState<'results' | 'exports' | 'shares'>('results');
  const [dataPage, setDataPage] = useState(1);
  const dataPageSize = 20;
  const [dataItems, setDataItems] = useState<Array<AdminResultItem | AdminExportItem | AdminShareItem>>([]);
  const [dataTotal, setDataTotal] = useState(0);
  const [dataLoading, setDataLoading] = useState(false);
  const [dataSelected, setDataSelected] = useState<Record<string, boolean>>({});
  const [dataBulkOpen, setDataBulkOpen] = useState(false);
  const [dataBulkAction, setDataBulkAction] = useState<'RESULT_DELETE' | 'EXPORT_DELETE' | 'SHARE_REVOKE' | 'SHARE_DELETE'>('RESULT_DELETE');
  const [dataBulkConfirm, setDataBulkConfirm] = useState('');
  const [dataBulkRunning, setDataBulkRunning] = useState(false);

  const [auditQ, setAuditQ] = useState('');
  const [auditAction, setAuditAction] = useState('');
  const [auditPage, setAuditPage] = useState(1);
  const auditPageSize = 50;
  const [auditItems, setAuditItems] = useState<AdminAuditLogItem[]>([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditLoading, setAuditLoading] = useState(false);

  const [sysCfg, setSysCfg] = useState<SystemConfig | null>(null);
  const [sysEnvFile, setSysEnvFile] = useState('');
  const [sysLoading, setSysLoading] = useState(false);
  const [sysSaving, setSysSaving] = useState(false);
  const [siteIconFile, setSiteIconFile] = useState<File | null>(null);
  const [siteIconUploading, setSiteIconUploading] = useState(false);
  const [siteIconTs, setSiteIconTs] = useState(() => Date.now());

  const [cfgOpen, setCfgOpen] = useState(false);
  const [cfgUser, setCfgUser] = useState<AdminUser | null>(null);
  const [userAiCfg, setUserAiCfg] = useState<any>(null);
  const [userCozeCfg, setUserCozeCfg] = useState<any>(null);
  const [cfgLoading, setCfgLoading] = useState(false);

  const [rtSearch, setRtSearch] = useState('');
  const [rtLoading, setRtLoading] = useState(false);
  const [rtUsers, setRtUsers] = useState<AdminRealtimeUser[]>([]);
  const [rtTotal, setRtTotal] = useState(0);
  const [rtTrend, setRtTrend] = useState<AdminRealtimeTrendPoint[]>([]);
  const [rtOnlineCount, setRtOnlineCount] = useState(0);
  const [rtRefreshedAt, setRtRefreshedAt] = useState<number | null>(null);
  const [mtEditor, setMtEditor] = useState('<p><strong>系统维护通知：</strong>请稍后重试，如有问题请联系管理员。</p>');
  const [mtSending, setMtSending] = useState(false);
  const [mtPreviewOpen, setMtPreviewOpen] = useState(false);
  const [mtHistory, setMtHistory] = useState<AdminMaintenanceHistoryItem[]>([]);
  const [mtBlacklistText, setMtBlacklistText] = useState('');
  const [mtSyncingBlacklist, setMtSyncingBlacklist] = useState(false);

  const loadUsers = async () => {
    setLoading(true);
    setMsg(null);
    try {
      const qs = new URLSearchParams();
      qs.set('page', String(page));
      qs.set('pageSize', String(pageSize));
      if (q.trim()) qs.set('q', q.trim());
      const r = await apiFetch<{ ok: boolean; items: AdminUser[]; total: number }>(`/api/admin/users?${qs.toString()}`, { method: 'GET' });
      setItems(r.items || []);
      setTotal(Number(r.total || 0));
      setSelected({});
    } catch (e) {
      setItems([]);
      setTotal(0);
      setMsg('加载失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const loadRealtime = async (qInput?: string) => {
    setRtLoading(true);
    try {
      const qs = new URLSearchParams();
      const qv = (qInput ?? rtSearch).trim();
      if (qv) qs.set('q', qv);
      const r = await apiFetch<{
        ok: boolean;
        total: number;
        items: AdminRealtimeUser[];
        trend: AdminRealtimeTrendPoint[];
        refreshedAt: number;
      }>(`/api/admin/realtime/online-users?${qs.toString()}`, { method: 'GET' });
      setRtUsers(r.items || []);
      setRtTotal(Number(r.total || 0));
      setRtTrend(Array.isArray(r.trend) ? r.trend : []);
      setRtOnlineCount(Number(r.total || 0));
      setRtRefreshedAt(Number(r.refreshedAt || Date.now()));
    } catch {
      setRtUsers([]);
      setRtTotal(0);
      setRtTrend([]);
      setRtOnlineCount(0);
    } finally {
      setRtLoading(false);
    }
  };

  const loadMessageHistory = async () => {
    try {
      const r = await apiFetch<{ ok: boolean; items: AdminMaintenanceHistoryItem[] }>('/api/admin/realtime/message-history?limit=30', { method: 'GET' });
      setMtHistory(r.items || []);
    } catch {
      setMtHistory([]);
    }
  };

  const loadBlacklist = async () => {
    try {
      const r = await apiFetch<{ ok: boolean; userIds: string[] }>('/api/admin/realtime/message-blacklist', { method: 'GET' });
      setMtBlacklistText((r.userIds || []).join(','));
    } catch {
      setMtBlacklistText('');
    }
  };

  const kickOnlineUser = async (sessionId: string, nickname: string) => {
    const ok = await confirm({
      title: '强制下线',
      message: `确认强制下线该在线用户？\n${nickname}`,
      confirmText: '确认下线'
    });
    if (!ok) return;
    try {
      await apiFetch('/api/admin/realtime/force-offline', { method: 'POST', body: JSON.stringify({ sessionId }) });
      setMsg('已执行强制下线');
      loadRealtime().catch(() => {});
    } catch {
      setMsg('强制下线失败，请稍后重试');
    }
  };

  const syncBlacklist = async () => {
    setMtSyncingBlacklist(true);
    try {
      const ids = mtBlacklistText
        .split(/[,\s\n]+/)
        .map((v) => v.trim())
        .filter(Boolean);
      await apiFetch('/api/admin/realtime/message-blacklist', { method: 'PUT', body: JSON.stringify({ add: ids, remove: [] }) });
      setMsg('黑名单已同步');
    } catch {
      setMsg('黑名单同步失败');
    } finally {
      setMtSyncingBlacklist(false);
    }
  };

  const sendMaintenance = async () => {
    const html = String(mtEditor || '').trim();
    if (!html) {
      setMsg('请先输入消息内容');
      return;
    }
    const ok = await confirm({
      title: '一键发送维护消息',
      message: '确认立即向全部在线用户发送高优先级维护消息？',
      confirmText: '确认发送'
    });
    if (!ok) return;
    setMtSending(true);
    try {
      const blacklistUserIds = mtBlacklistText
        .split(/[,\s\n]+/)
        .map((v) => v.trim())
        .filter(Boolean);
      const r = await apiFetch<{ ok: boolean; successCount: number; failCount: number; filteredCount: number }>('/api/admin/realtime/broadcast-maintenance', {
        method: 'POST',
        body: JSON.stringify({ html, blacklistUserIds })
      });
      setMsg(`发送完成：成功 ${Number(r.successCount || 0)}，失败 ${Number(r.failCount || 0)}，过滤 ${Number(r.filteredCount || 0)}`);
      loadMessageHistory().catch(() => {});
    } catch (e: any) {
      if (e instanceof ApiError && e.payload?.error === 'DUPLICATE_IN_5_MIN') {
        setMsg('同一内容 5 分钟内不可重复发送');
      } else {
        setMsg('发送失败，请稍后重试');
      }
    } finally {
      setMtSending(false);
    }
  };

  const onTabContextAction = async (tab: typeof adminTab) => {
    const cmd = await prompt({
      title: '标签快捷操作',
      message: `当前标签：${tab}\n输入命令：realtime/users/system/logs/stats/reset`,
      defaultValue: tab
    });
    const c = String(cmd || '').trim().toLowerCase();
    if (!c) return;
    if (c === 'reset') {
      setAdminTab('realtime');
      return;
    }
    if (c === 'realtime' || c === 'users' || c === 'system' || c === 'logs' || c === 'stats') {
      setAdminTab(c);
    }
  };

  const loadFailures = async () => {
    const email = failEmail.trim().toLowerCase();
    if (!email) {
      setFailState(null);
      setFailItems([]);
      setFailTotal(0);
      return;
    }
    setFailLoading(true);
    try {
      const qs = new URLSearchParams();
      qs.set('email', email);
      qs.set('page', String(failPage));
      qs.set('pageSize', String(failPageSize));
      const r = await apiFetch<{ ok: boolean; items: LoginFailureEvent[]; total: number }>(`/api/admin/login-failures?${qs.toString()}`, { method: 'GET' });
      setFailItems(r.items || []);
      setFailTotal(Number(r.total || 0));
    } catch {
      setFailItems([]);
      setFailTotal(0);
    } finally {
      setFailLoading(false);
    }
  };

  const loadFailureState = async () => {
    const email = failEmail.trim().toLowerCase();
    if (!email) {
      setFailState(null);
      return;
    }
    setFailStateLoading(true);
    try {
      const qs = new URLSearchParams();
      qs.set('email', email);
      const r = await apiFetch<{ ok: boolean; row: LoginFailureState | null }>(`/api/admin/login-failures-state?${qs.toString()}`, { method: 'GET' });
      setFailState(r.row || null);
    } catch {
      setFailState(null);
    } finally {
      setFailStateLoading(false);
    }
  };

  const loadUserData = async () => {
    if (!dataUser) return;
    setDataLoading(true);
    try {
      const qs = new URLSearchParams();
      qs.set('page', String(dataPage));
      qs.set('pageSize', String(dataPageSize));
      if (dataTab === 'results') {
        const r = await apiFetch<{ ok: boolean; items: AdminResultItem[]; total: number }>(`/api/admin/users/${encodeURIComponent(dataUser.id)}/results?${qs.toString()}`, { method: 'GET' });
        setDataItems(r.items || []);
        setDataTotal(Number(r.total || 0));
      } else if (dataTab === 'exports') {
        const r = await apiFetch<{ ok: boolean; items: AdminExportItem[]; total: number }>(`/api/admin/users/${encodeURIComponent(dataUser.id)}/exports?${qs.toString()}`, { method: 'GET' });
        setDataItems(r.items || []);
        setDataTotal(Number(r.total || 0));
      } else {
        const r = await apiFetch<{ ok: boolean; items: AdminShareItem[]; total: number }>(`/api/admin/users/${encodeURIComponent(dataUser.id)}/shares?${qs.toString()}`, { method: 'GET' });
        setDataItems(r.items || []);
        setDataTotal(Number(r.total || 0));
      }
    } catch {
      setDataItems([]);
      setDataTotal(0);
    } finally {
      setDataLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [page, q]);

  useEffect(() => {
    loadFailures();
  }, [failPage]);

  useEffect(() => {
    if (!dataOpen) return;
    setDataSelected({});
    setDataBulkOpen(false);
    setDataBulkConfirm('');
    loadUserData();
  }, [dataOpen, dataUser?.id, dataTab, dataPage]);

  useEffect(() => {
    setAuditPage(1);
  }, [auditQ, auditAction]);

  useEffect(() => {
    setAuditLoading(true);
    setMsg(null);
    (async () => {
      try {
        const qs = new URLSearchParams();
        qs.set('page', String(auditPage));
        qs.set('pageSize', String(auditPageSize));
        if (auditQ.trim()) qs.set('q', auditQ.trim());
        if (auditAction) qs.set('action', auditAction);
        const r = await apiFetch<{ ok: boolean; items: AdminAuditLogItem[]; total: number }>(`/api/admin/audit-logs?${qs.toString()}`, { method: 'GET' });
        setAuditItems(r.items || []);
        setAuditTotal(Number(r.total || 0));
      } catch {
        setAuditItems([]);
        setAuditTotal(0);
      } finally {
        setAuditLoading(false);
      }
    })();
  }, [auditPage, auditQ, auditAction]);

  useEffect(() => {
    setSysLoading(true);
    (async () => {
      try {
        const r = await apiFetch<{ ok: boolean; envFile: string; config: SystemConfig }>('/api/admin/system-config', { method: 'GET' });
        setSysCfg(r.config);
        setSysEnvFile(String(r.envFile || ''));
      } catch {
        setSysCfg(null);
        setSysEnvFile('');
      } finally {
        setSysLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    loadRealtime().catch(() => {});
    loadMessageHistory().catch(() => {});
    loadBlacklist().catch(() => {});
    const timer = window.setInterval(() => {
      loadRealtime().catch(() => {});
    }, 5000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!e.altKey) return;
      if (e.key === '1') setAdminTab('realtime');
      if (e.key === '2') setAdminTab('users');
      if (e.key === '3') setAdminTab('system');
      if (e.key === '4') setAdminTab('logs');
      if (e.key === '5') setAdminTab('stats');
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const failPages = Math.max(1, Math.ceil(failTotal / failPageSize));
  const dataPages = Math.max(1, Math.ceil(dataTotal / dataPageSize));
  const auditPages = Math.max(1, Math.ceil(auditTotal / auditPageSize));
  const selectedIds = Object.entries(selected)
    .filter(([, v]) => v)
    .map(([k]) => k);
  const selectedCount = selectedIds.length;
  const needConfirm = bulkAction === 'DELETE' ? 'DELETE' : 'CONFIRM';
  const confirmOk = bulkConfirm.trim().toUpperCase() === needConfirm;

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div className="section-title">
        <h3>管理</h3>
        <span className="muted">仅管理员可见；高危操作，请谨慎</span>
      </div>
      <div style={{ display: 'grid', gap: 12 }}>
      <div className="tool">
        <div className="tool-top">
          <div className="tool-name">管理标签页</div>
          <span className="badge">Alt+1~5 快捷切换</span>
        </div>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
          {[
            ['realtime', '实时管理'],
            ['users', '用户管理'],
            ['system', '系统配置'],
            ['logs', '日志审计'],
            ['stats', '数据统计']
          ].map(([key, label]) => (
            <button
              key={key}
              className={`btn ${adminTab === key ? '' : 'secondary'}`}
              type="button"
              onClick={() => setAdminTab(key as any)}
              onContextMenu={(e) => {
                e.preventDefault();
                onTabContextAction(key as any).catch(() => {});
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {adminTab === 'realtime' ? (
        <>
          <div className="tool">
            <div className="tool-top">
              <div className="tool-name">在线用户实时监控</div>
              <span className="badge">{rtLoading ? '刷新中…' : `在线 ${rtOnlineCount} 人`}</span>
            </div>
            <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
              <input
                value={rtSearch}
                onChange={(e) => setRtSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') loadRealtime(e.currentTarget.value).catch(() => {});
                }}
                placeholder="按昵称/邮箱搜索在线用户"
                style={{ minWidth: 260, flex: 1 }}
              />
              <button className="btn secondary" type="button" onClick={() => loadRealtime().catch(() => {})}>
                刷新
              </button>
            </div>
            <div className="muted" style={{ marginTop: 8 }}>最后刷新：{fmt(rtRefreshedAt)} · 每 5 秒自动刷新</div>
            <div className="table-wrap" style={{ marginTop: 10 }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>昵称</th>
                    <th>邮箱</th>
                    <th>在线时长</th>
                    <th>登录 IP</th>
                    <th>浏览器</th>
                    <th>最后操作</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {(rtUsers || []).map((u) => (
                    <tr key={u.sessionId} style={u.onlineSeconds > 6 * 3600 ? { background: 'rgba(239,68,68,0.14)' } : undefined}>
                      <td>{u.nickname}</td>
                      <td>{u.emailMasked}</td>
                      <td>{fmtOnline(u.onlineSeconds)}</td>
                      <td>{u.loginIp || '-'}</td>
                      <td title={u.browser || '-'}>{String(u.browser || '-').slice(0, 28)}</td>
                      <td>{fmt(u.lastActionAt)}</td>
                      <td>
                        <button className="btn secondary" type="button" onClick={() => kickOnlineUser(u.sessionId, u.nickname).catch(() => {})}>
                          强制下线
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!rtLoading && !rtUsers.length ? (
                    <tr>
                      <td colSpan={7} className="muted">暂无在线用户</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>

          <div className="tool">
            <div className="tool-top">
              <div className="tool-name">在线趋势（24小时）</div>
              <span className="badge">趋势样本 {rtTrend.length}</span>
            </div>
            <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: `repeat(${Math.max(1, rtTrend.length)}, minmax(0,1fr))`, gap: 4, alignItems: 'end', height: 130 }}>
              {(rtTrend || []).map((p) => {
                const max = Math.max(1, ...rtTrend.map((x) => Number(x.count || 0)));
                const h = Math.max(6, Math.round((Number(p.count || 0) / max) * 112));
                return <div key={p.at} title={`${new Date(p.at).toLocaleTimeString()} · ${p.count}人`} style={{ background: 'rgba(34,211,238,0.42)', borderRadius: 6, height: h }} />;
              })}
            </div>
          </div>

          <div className="tool">
            <div className="tool-top">
              <div className="tool-name">维护消息群发</div>
              <span className="badge">高优先级 · 强制弹窗</span>
            </div>
            <div className="field" style={{ marginTop: 10 }}>
              <div className="muted">富文本内容（支持基础 HTML 标签）</div>
              <textarea value={mtEditor} onChange={(e) => setMtEditor(e.target.value)} rows={6} />
              <div className="muted" style={{ marginTop: 6 }}>字数：{String(mtEditor || '').replace(/<[^>]+>/g, '').trim().length}</div>
            </div>
            <div className="field" style={{ marginTop: 10 }}>
              <div className="muted">黑名单用户（userId，逗号/空格分隔）</div>
              <textarea value={mtBlacklistText} onChange={(e) => setMtBlacklistText(e.target.value)} rows={2} />
              <div className="row" style={{ gap: 8, marginTop: 8 }}>
                <button className="btn secondary" type="button" onClick={() => setMtPreviewOpen(true)}>
                  发送预览
                </button>
                <button className="btn secondary" type="button" disabled={mtSyncingBlacklist} onClick={() => syncBlacklist().catch(() => {})}>
                  {mtSyncingBlacklist ? '同步中…' : '同步黑名单'}
                </button>
                <button className="btn" type="button" disabled={mtSending} onClick={() => sendMaintenance().catch(() => {})}>
                  {mtSending ? '发送中…' : '一键发送'}
                </button>
              </div>
            </div>
            <div className="muted" style={{ marginTop: 10 }}>历史消息</div>
            <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
              {(mtHistory || []).map((h) => (
                <div key={h.id} className="card" style={{ padding: 10 }}>
                  <div style={{ fontWeight: 800 }}>{h.text.slice(0, 120)}</div>
                  <div className="muted" style={{ marginTop: 6 }}>
                    {fmt(h.sentAt)} · 成功 {h.successCount} / 失败 {h.failCount} / 过滤 {h.filteredCount}
                  </div>
                </div>
              ))}
              {!mtHistory.length ? <div className="muted">暂无记录</div> : null}
            </div>
          </div>
        </>
      ) : null
      }

      <div className="tool">
        <div className="tool-top">
          <div className="tool-name">操作说明</div>
          <span className="badge">避免误操作</span>
        </div>
        <div className="muted" style={{ lineHeight: 1.8, marginTop: 10 }}>
          - 注销：账号不可再登录，但数据仍保留（历史/导出/分享等）。
          <br />- 删除：彻底物理删除账号与关联数据，无法恢复。
          <br />- 重置密码：无需邮箱验证码，执行后会清理该用户登录态（需要重新登录）。
          <br />- 快速注册：跳过邮箱验证码，直接创建账号。
          <br />- 编辑资料：可直接修改邮箱/昵称（管理员不受 7 天限频）。
        </div>
      </div>

      <div className="tool">
        <div className="tool-top">
          <div className="tool-name">系统配置</div>
          <span className="badge">{sysLoading ? '加载中…' : '写入 .env'}</span>
        </div>
        <div className="muted" style={{ lineHeight: 1.8, marginTop: 10 }}>
          - 本页仅允许修改非敏感站点配置（PUBLIC_BASE_URL / 备案 / 超时等），并写入后端 `.env`
          <br />- 配置文件：{sysEnvFile || '-'}
          <br />- 填写规则：不需要加引号；不要换行；保存后通常立即生效
        </div>
        {sysCfg ? (
          <div style={{ marginTop: 12 }}>
            <div className="field">
              <div className="muted">PUBLIC_BASE_URL（站点对外域名）</div>
              <input value={sysCfg.publicBaseUrl} onChange={(e) => setSysCfg({ ...sysCfg, publicBaseUrl: e.target.value })} placeholder="https://example.com" />
              <div className="muted" style={{ marginTop: 6, lineHeight: 1.7 }}>
                用于生成分享链接、邮件中的回跳链接等。必须包含协议（http/https）。推荐不带尾部 /。
                <br />示例：<span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, \"Liberation Mono\", \"Courier New\", monospace' }}>https://your-domain.com</span>
              </div>
            </div>
            <div className="field" style={{ marginTop: 12 }}>
              <div className="muted">ICP_TEXT（页脚备案文本）</div>
              <input value={sysCfg.icpText} onChange={(e) => setSysCfg({ ...sysCfg, icpText: e.target.value })} placeholder="京ICP备xxxx号-x" />
              <div className="muted" style={{ marginTop: 6, lineHeight: 1.7 }}>
                页脚展示用。没有备案可留空。
              </div>
            </div>
            <div className="field" style={{ marginTop: 12 }}>
              <div className="muted">ICP_URL（备案链接）</div>
              <input value={sysCfg.icpUrl} onChange={(e) => setSysCfg({ ...sysCfg, icpUrl: e.target.value })} placeholder="https://beian.miit.gov.cn/" />
              <div className="muted" style={{ marginTop: 6, lineHeight: 1.7 }}>
                点击备案文本跳转的链接。通常填写 <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, \"Liberation Mono\", \"Courier New\", monospace' }}>https://beian.miit.gov.cn/</span>。
              </div>
            </div>
            <div className="field" style={{ marginTop: 12 }}>
              <div className="muted">MPS_TEXT（公安网备文本）</div>
              <input value={sysCfg.mpsText} onChange={(e) => setSysCfg({ ...sysCfg, mpsText: e.target.value })} placeholder="京公网安备 11010102000001号" />
              <div className="muted" style={{ marginTop: 6, lineHeight: 1.7 }}>
                页脚展示用。示例：<span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, \"Liberation Mono\", \"Courier New\", monospace' }}>京公网安备 11010102000001号</span>
              </div>
            </div>
            <div className="field" style={{ marginTop: 12 }}>
              <div className="muted">MPS_URL（公安网备查询链接）</div>
              <input
                value={sysCfg.mpsUrl}
                onChange={(e) => setSysCfg({ ...sysCfg, mpsUrl: e.target.value })}
                placeholder="https://beian.mps.gov.cn/#/query/webSearch"
              />
              <div className="muted" style={{ marginTop: 6, lineHeight: 1.7 }}>
                点击公安网备文本跳转的链接。通常填写 <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, \"Liberation Mono\", \"Courier New\", monospace' }}>https://beian.mps.gov.cn/#/query/webSearch</span>。
              </div>
            </div>
            <div className="row" style={{ gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
              <div className="field" style={{ width: 260 }}>
                <div className="muted">COOKIE_SECURE（是否仅 HTTPS）</div>
                <select value={sysCfg.cookieSecure} onChange={(e) => setSysCfg({ ...sysCfg, cookieSecure: e.target.value })}>
                  <option value="">自动</option>
                  <option value="true">true</option>
                  <option value="false">false</option>
                </select>
                <div className="muted" style={{ marginTop: 6, lineHeight: 1.7 }}>
                  生产环境强烈建议 <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, \"Liberation Mono\", \"Courier New\", monospace' }}>true</span>（仅 HTTPS 发送 Cookie）。
                  <br />本地 http 开发可选 <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, \"Liberation Mono\", \"Courier New\", monospace' }}>false</span>。
                </div>
              </div>
              <div className="field" style={{ width: 260 }}>
                <div className="muted">AI_REQUEST_TIMEOUT_MS（AI 请求超时，毫秒）</div>
                <input value={sysCfg.aiRequestTimeoutMs} onChange={(e) => setSysCfg({ ...sysCfg, aiRequestTimeoutMs: e.target.value.replace(/[^\d]/g, '').slice(0, 6) })} />
                <div className="muted" style={{ marginTop: 6, lineHeight: 1.7 }}>
                  控制 AI 上游请求最长等待时间。过小可能导致频繁超时，过大可能让用户等待更久。
                  <br />示例：<span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, \"Liberation Mono\", \"Courier New\", monospace' }}>120000</span>（2 分钟）
                </div>
              </div>
              <div className="field" style={{ flex: 1, minWidth: 280 }}>
                <div className="muted">LUNISOLAR_SERVICE_URL（农历/历法服务地址）</div>
                <input value={sysCfg.lunisolarServiceUrl} onChange={(e) => setSysCfg({ ...sysCfg, lunisolarServiceUrl: e.target.value })} placeholder="http://127.0.0.1:3100" />
                <div className="muted" style={{ marginTop: 6, lineHeight: 1.7 }}>
                  后端调用内部服务的地址。一般是本机端口或内网地址。
                  <br />示例：<span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, \"Liberation Mono\", \"Courier New\", monospace' }}>http://127.0.0.1:3100</span>
                </div>
              </div>
            </div>
            <div className="field" style={{ marginTop: 12 }}>
              <div className="muted">ADMIN_EMAIL（管理员邮箱，逗号分隔）</div>
              <input value={sysCfg.adminEmail} onChange={(e) => setSysCfg({ ...sysCfg, adminEmail: e.target.value })} placeholder="admin@example.com,admin2@example.com" />
              <div className="muted" style={{ marginTop: 6, lineHeight: 1.7 }}>
                这些邮箱登录后可看到“管理”页。多个邮箱用英文逗号分隔，忽略大小写。
                <br />示例：<span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, \"Liberation Mono\", \"Courier New\", monospace' }}>a@b.com,c@d.com</span>
              </div>
            </div>

            <div className="field" style={{ marginTop: 12 }}>
              <div className="muted">站点图标（favicon）</div>
              <div className="row" style={{ gap: 12, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
                <img
                  src={`/api/public/site-icon?ts=${siteIconTs}`}
                  alt="site icon"
                  style={{ width: 44, height: 44, borderRadius: 12, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.06)' }}
                />
                <input
                  type="file"
                  accept="image/png"
                  onChange={(e) => {
                    const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                    setSiteIconFile(f);
                  }}
                />
              </div>
              <div className="muted" style={{ marginTop: 6, lineHeight: 1.7 }}>
                推荐 PNG：256×256，透明背景，文件大小 ≤ 512KB。上传后会立即生效（可能需要刷新浏览器标签图标）。
              </div>
              <div className="row" style={{ marginTop: 10, justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
                <button
                  className="btn secondary"
                  type="button"
                  disabled={siteIconUploading || !siteIconFile}
                  onClick={async () => {
                    if (!siteIconFile) return;
                    setSiteIconUploading(true);
                    setMsg(null);
                    try {
                      const dataUrl: string = await new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onerror = () => reject(new Error('READ_FAILED'));
                        reader.onload = () => resolve(String(reader.result || ''));
                        reader.readAsDataURL(siteIconFile);
                      });
                      await apiFetch('/api/admin/site-icon', { method: 'PUT', body: JSON.stringify({ dataUrl }) });
                      setMsg('图标已更新');
                      setSiteIconTs(Date.now());
                      setSiteIconFile(null);
                    } catch (e) {
                      if (e instanceof ApiError) {
                        const c = (e.body as any)?.error;
                        if (c === 'ICON_TOO_LARGE') setMsg('上传失败：图标过大（≤ 512KB）');
                        else setMsg('上传失败，请稍后重试');
                      } else setMsg('上传失败，请稍后重试');
                    } finally {
                      setSiteIconUploading(false);
                    }
                  }}
                >
                  {siteIconUploading ? '上传中…' : '上传图标'}
                </button>
                <button
                  className="btn danger"
                  type="button"
                  disabled={siteIconUploading}
                  onClick={async () => {
                    const ok = await confirm({ title: '恢复默认图标', message: '确认恢复默认图标？', confirmText: '确认恢复' });
                    if (!ok) return;
                    setSiteIconUploading(true);
                    setMsg(null);
                    try {
                      await apiFetch('/api/admin/site-icon', { method: 'DELETE' });
                      setMsg('已恢复默认图标');
                      setSiteIconTs(Date.now());
                    } catch {
                      setMsg('操作失败，请稍后重试');
                    } finally {
                      setSiteIconUploading(false);
                    }
                  }}
                >
                  恢复默认
                </button>
              </div>
            </div>
            <div className="row" style={{ marginTop: 12, justifyContent: 'flex-end' }}>
              <button
                className="btn"
                disabled={sysSaving}
                onClick={async () => {
                  if (!sysCfg) return;
                  const ok = await confirm({ title: '保存系统配置', message: '确认保存系统配置？', confirmText: '确认保存' });
                  if (!ok) return;
                  setSysSaving(true);
                  setMsg(null);
                  try {
                    await apiFetch('/api/admin/system-config', { method: 'PUT', body: JSON.stringify({ config: sysCfg }) });
                    setMsg('保存成功');
                  } catch (e) {
                    if (e instanceof ApiError) {
                      const c = (e.body as any)?.error;
                      if (c === 'SAVE_FAILED') setMsg('保存失败（无法写入 .env）');
                      else setMsg('保存失败，请稍后重试');
                    } else setMsg('保存失败，请稍后重试');
                  } finally {
                    setSysSaving(false);
                  }
                }}
                type="button"
              >
                {sysSaving ? '保存中…' : '保存'}
              </button>
            </div>
          </div>
        ) : (
          <div className="muted" style={{ marginTop: 10 }}>
            {sysLoading ? '加载中…' : '加载失败（需要管理员权限，或后端未更新）'}
          </div>
        )}
      </div>

      <div className="tool">
        <div className="tool-top">
          <div className="tool-name">快速注册</div>
          <span className="badge">跳过邮箱验证</span>
        </div>
        <div className="field" style={{ marginTop: 10 }}>
          <div className="muted">邮箱</div>
          <input value={createEmail} onChange={(e) => setCreateEmail(e.target.value.replace(/\s+/g, '').slice(0, 254))} placeholder="name@example.com" />
        </div>
        <div className="field" style={{ marginTop: 12 }}>
          <div className="muted">密码（8-64，仅字母/数字/下划线）</div>
          <input value={createPassword} onChange={(e) => setCreatePassword(e.target.value.slice(0, 64))} type="password" autoComplete="new-password" />
        </div>
        <div className="row" style={{ marginTop: 12, justifyContent: 'flex-end' }}>
          <button
            className="btn"
            disabled={creating}
            onClick={async () => {
              setCreating(true);
              setMsg(null);
              try {
                await apiFetch('/api/admin/users', { method: 'POST', body: JSON.stringify({ email: createEmail, password: createPassword }) });
                setMsg('注册成功');
                setCreateEmail('');
                setCreatePassword('');
                setPage(1);
                await loadUsers();
              } catch (e) {
                if (e instanceof ApiError) {
                  const c = (e.body as any)?.error;
                  if (c === 'EMAIL_EXISTS') setMsg('该邮箱已注册');
                  else if (c === 'INVALID_EMAIL') setMsg('邮箱格式不正确');
                  else if (c === 'INVALID_PASSWORD_FORMAT') setMsg('密码格式不正确（仅字母/数字/下划线）');
                  else if (c === 'WEAK_PASSWORD') setMsg('密码强度不足（至少 8 位）');
                  else setMsg('注册失败，请稍后重试');
                } else setMsg('注册失败，请稍后重试');
              } finally {
                setCreating(false);
              }
            }}
            type="button"
          >
            {creating ? '创建中…' : '创建账号'}
          </button>
        </div>
      </div>

      <div className="tool">
        <div className="tool-top">
          <div className="tool-name">账户列表</div>
          <span className="badge">{loading ? '加载中…' : `${total} 个`}</span>
        </div>

        <div className="row" style={{ marginTop: 10, gap: 10, flexWrap: 'wrap' }}>
          <input style={{ width: 280 }} value={q} onChange={(e) => setQ(e.target.value)} placeholder="按邮箱搜索（模糊）" />
          <button className="btn secondary" onClick={() => loadUsers()} disabled={loading} type="button">
            刷新
          </button>
          <button className="btn secondary" disabled={loading || !selectedCount} onClick={() => setBulkOpen(true)} type="button">
            批量操作（{selectedCount}）
          </button>
          <div className="muted" style={{ marginLeft: 'auto' }}>
            第 {page}/{totalPages} 页
          </div>
          <button className="btn secondary" disabled={loading || page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} type="button">
            上一页
          </button>
          <button className="btn secondary" disabled={loading || page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} type="button">
            下一页
          </button>
        </div>

        <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
          {items.map((u) => (
            <div key={u.id} style={{ border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14, padding: 12 }}>
              <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                <div style={{ display: 'grid', gap: 4 }}>
                  <div className="row" style={{ gap: 10, alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={!!selected[u.id]}
                      onChange={(e) => setSelected((s) => ({ ...s, [u.id]: e.target.checked }))}
                      style={{ width: 16, height: 16 }}
                    />
                    <div style={{ fontWeight: 900 }}>{u.email}</div>
                  </div>
                  <div className="muted" style={{ lineHeight: 1.7 }}>
                    id：{u.id}
                    <br />
                    注册：{fmt(u.createdAt)}
                    <br />
                    昵称：{String(u.nickname || '').trim() || '-'}
                    <br />
                    在线时长：{fmtOnline(u.onlineTotalSeconds)}
                    <br />
                    最后活跃：{fmt(u.lastActiveAt || null)}
                    <br />
                    状态：{Number(u.status || 1) === 2 ? `已注销（${fmt(u.canceledAt || null)}）` : '正常'}
                  </div>
                </div>
                <div className="row" style={{ gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <button
                    className="btn secondary"
                    type="button"
                    onClick={() => {
                      setEditUser(u);
                      setEditEmail(u.email);
                      setEditNickname(String(u.nickname || '').trim());
                      setEditOpen(true);
                    }}
                  >
                    编辑
                  </button>
                  <button
                    className="btn secondary"
                    type="button"
                    onClick={async () => {
                      setCfgUser(u);
                      setCfgOpen(true);
                      setCfgLoading(true);
                      setMsg(null);
                      try {
                        const [ai, coze] = await Promise.all([
                          apiFetch<{ ok: boolean; config: any }>(`/api/admin/users/${encodeURIComponent(u.id)}/ai-config`, { method: 'GET' }),
                          apiFetch<{ ok: boolean; config: any }>(`/api/admin/users/${encodeURIComponent(u.id)}/coze-config`, { method: 'GET' })
                        ]);
                        setUserAiCfg(ai.config);
                        setUserCozeCfg(coze.config);
                      } catch {
                        setUserAiCfg(null);
                        setUserCozeCfg(null);
                      } finally {
                        setCfgLoading(false);
                      }
                    }}
                  >
                    配置
                  </button>
                  <button
                    className="btn secondary"
                    type="button"
                    onClick={() => {
                      setDataUser(u);
                      setDataTab('results');
                      setDataPage(1);
                      setDataOpen(true);
                    }}
                  >
                    数据
                  </button>
                  <button
                    className="btn"
                    type="button"
                    onClick={() => {
                      setPwUser(u);
                      setPwNew('');
                      setPwOpen(true);
                    }}
                  >
                    重置密码
                  </button>
                  <button
                    className="btn secondary"
                    type="button"
                    onClick={async () => {
                      if (Number(u.status || 1) === 2) {
                        const ok = await confirm({ title: '恢复账号', message: `确认恢复账号？\n${u.email}\n恢复后可再次登录。`, confirmText: '确认恢复' });
                        if (!ok) return;
                        setMsg(null);
                        try {
                          await apiFetch(`/api/admin/users/${encodeURIComponent(u.id)}/restore`, { method: 'POST', body: JSON.stringify({}) });
                          setMsg('已恢复');
                          await loadUsers();
                        } catch {
                          setMsg('操作失败，请稍后重试');
                        }
                        return;
                      }
                      const ok = await confirmCancelAccount({
                        title: '注销账号',
                        message: `确认注销账号？\n${u.email}\n注销后不可登录，但数据仍保留。`,
                        confirmText: '确认注销'
                      });
                      if (!ok) return;
                      setMsg(null);
                      try {
                        await apiFetch(`/api/admin/users/${encodeURIComponent(u.id)}/cancel`, { method: 'POST', body: JSON.stringify({}) });
                        setMsg('已注销');
                        await loadUsers();
                      } catch {
                        setMsg('操作失败，请稍后重试');
                      }
                    }}
                  >
                    {Number(u.status || 1) === 2 ? '恢复' : '注销'}
                  </button>
                  <button
                    className="btn danger"
                    type="button"
                    onClick={async () => {
                      const ok = await confirmDelete({
                        title: '删除账号',
                        message: `确认删除账号？\n${u.email}\n删除后数据不可恢复。`,
                        confirmText: '确认删除'
                      });
                      if (!ok) return;
                      setMsg(null);
                      try {
                        await apiFetch(`/api/admin/users/${encodeURIComponent(u.id)}`, { method: 'DELETE' });
                        setMsg('已删除');
                        await loadUsers();
                      } catch {
                        setMsg('删除失败，请稍后重试');
                      }
                    }}
                  >
                    删除
                  </button>
                </div>
              </div>
            </div>
          ))}
          {!items.length && !loading ? <div className="muted">暂无数据</div> : null}
        </div>
      </div>

      <div className="tool">
        <div className="tool-top">
          <div className="tool-name">登录失败原因查询</div>
          <span className="badge">{failLoading ? '加载中…' : failTotal ? `${failTotal} 条` : '0 条'}</span>
        </div>
        <div className="row" style={{ marginTop: 10, gap: 10, flexWrap: 'wrap' }}>
          <input style={{ width: 320 }} value={failEmail} onChange={(e) => setFailEmail(e.target.value.replace(/\s+/g, '').slice(0, 254))} placeholder="输入邮箱后查询" />
          <button
            className="btn secondary"
            type="button"
            disabled={failLoading}
            onClick={() => {
              setFailPage(1);
              loadFailureState();
              loadFailures();
            }}
          >
            查询
          </button>
          <button
            className="btn secondary"
            type="button"
            disabled={failStateLoading || !failEmail.trim()}
            onClick={() => loadFailureState()}
          >
            刷新锁定
          </button>
          <div className="muted" style={{ marginLeft: 'auto' }}>
            第 {failPage}/{failPages} 页
          </div>
          <button className="btn secondary" disabled={failLoading || failPage <= 1} onClick={() => setFailPage((p) => Math.max(1, p - 1))} type="button">
            上一页
          </button>
          <button className="btn secondary" disabled={failLoading || failPage >= failPages} onClick={() => setFailPage((p) => Math.min(failPages, p + 1))} type="button">
            下一页
          </button>
        </div>
        <div className="muted" style={{ marginTop: 10, lineHeight: 1.8 }}>
          锁定状态：
          {failStateLoading ? (
            <span> 加载中…</span>
          ) : failState ? (
            <span>
              {' '}
              count={failState.count}，lockedUntil={failState.lockedUntil ? fmt(failState.lockedUntil) : '-'}
            </span>
          ) : (
            <span> -</span>
          )}
          {failState && (failState.lockedUntil || failState.count > 0) ? (
            <span>
              {' '}
              <button
                className="btn secondary"
                type="button"
                onClick={async () => {
                  const email = failEmail.trim().toLowerCase();
                  const ok = await confirm({ title: '解锁账号', message: `确认解锁并清空失败计数？\n${email}`, confirmText: '确认解锁' });
                  if (!ok) return;
                  setMsg(null);
                  try {
                    await apiFetch('/api/admin/login-failures-unlock', { method: 'POST', body: JSON.stringify({ email }) });
                    setMsg('已解锁');
                    await loadFailureState();
                    await loadFailures();
                  } catch {
                    setMsg('解锁失败，请稍后重试');
                  }
                }}
              >
                解锁
              </button>
            </span>
          ) : null}
        </div>
        <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
          {failItems.map((it) => (
            <div key={it.id} style={{ border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14, padding: 12 }}>
              <div style={{ fontWeight: 900 }}>{it.reason}</div>
              <div className="muted" style={{ lineHeight: 1.7 }}>
                时间：{fmt(it.createdAt)}
                <br />
                IP：{it.ip || '-'}
                <br />
                UA：{it.userAgent || '-'}
              </div>
            </div>
          ))}
          {!failItems.length && !failLoading ? <div className="muted">暂无记录</div> : null}
        </div>
      </div>

      </div>
      <Modal open={pwOpen} onClose={() => setPwOpen(false)} title="重置密码">
        <div className="muted" style={{ lineHeight: 1.8 }}>
          目标账号：{pwUser?.email || '-'}
        </div>
        <div className="field" style={{ marginTop: 12 }}>
          <div className="muted">新密码（8-64，仅字母/数字/下划线）</div>
          <input value={pwNew} onChange={(e) => setPwNew(e.target.value.slice(0, 64))} type="password" autoComplete="new-password" />
        </div>
        <div className="row" style={{ marginTop: 12, justifyContent: 'flex-end' }}>
          <button
            className="btn"
            disabled={pwSaving || !pwUser}
            onClick={async () => {
              if (!pwUser) return;
              const ok = await confirmReset({
                title: '重置密码',
                message: `确认重置密码？\n${pwUser.email}\n重置后该用户需要使用新密码重新登录。`,
                confirmText: '确认重置'
              });
              if (!ok) return;
              setPwSaving(true);
              setMsg(null);
              try {
                await apiFetch(`/api/admin/users/${encodeURIComponent(pwUser.id)}/password`, { method: 'POST', body: JSON.stringify({ newPassword: pwNew }) });
                setMsg('密码已重置');
                setPwOpen(false);
              } catch (e) {
                if (e instanceof ApiError) {
                  const c = (e.body as any)?.error;
                  if (c === 'WEAK_PASSWORD') setMsg('密码强度不足（至少 8 位）');
                  else if (c === 'INVALID_PASSWORD_FORMAT') setMsg('密码格式不正确（仅字母/数字/下划线）');
                  else setMsg('重置失败，请稍后重试');
                } else setMsg('重置失败，请稍后重试');
              } finally {
                setPwSaving(false);
              }
            }}
            type="button"
          >
            {pwSaving ? '保存中…' : '保存'}
          </button>
        </div>
      </Modal>

      <Modal open={editOpen} onClose={() => setEditOpen(false)} title="编辑资料">
        <div className="muted" style={{ lineHeight: 1.8 }}>
          目标账号：{editUser?.email || '-'}
        </div>
        <div className="field" style={{ marginTop: 12 }}>
          <div className="muted">邮箱</div>
          <input value={editEmail} onChange={(e) => setEditEmail(e.target.value.replace(/\s+/g, '').slice(0, 254))} />
        </div>
        <div className="field" style={{ marginTop: 12 }}>
          <div className="muted">昵称</div>
          <input value={editNickname} onChange={(e) => setEditNickname(e.target.value.slice(0, 32))} />
        </div>
        <div className="row" style={{ marginTop: 12, justifyContent: 'flex-end' }}>
          <button
            className="btn"
            disabled={editSaving || !editUser}
            onClick={async () => {
              if (!editUser) return;
              const ok = await confirm({ title: '保存资料', message: `确认保存资料？\n${editUser.email}`, confirmText: '确认保存' });
              if (!ok) return;
              setEditSaving(true);
              setMsg(null);
              try {
                await apiFetch(`/api/admin/users/${encodeURIComponent(editUser.id)}`, {
                  method: 'PUT',
                  body: JSON.stringify({ email: editEmail, nickname: editNickname })
                });
                setMsg('保存成功');
                setEditOpen(false);
                await loadUsers();
              } catch (e) {
                if (e instanceof ApiError) {
                  const c = (e.body as any)?.error;
                  if (c === 'EMAIL_EXISTS') setMsg('该邮箱已被占用');
                  else if (c === 'INVALID_EMAIL') setMsg('邮箱格式不正确');
                  else setMsg('保存失败，请稍后重试');
                } else setMsg('保存失败，请稍后重试');
              } finally {
                setEditSaving(false);
              }
            }}
            type="button"
          >
            {editSaving ? '保存中…' : '保存'}
          </button>
        </div>
      </Modal>

      <Modal open={dataOpen} onClose={() => setDataOpen(false)} title="用户数据">
        <div className="muted" style={{ lineHeight: 1.8 }}>
          目标账号：{dataUser?.email || '-'}
        </div>
        <div className="row" style={{ marginTop: 10, gap: 8, flexWrap: 'wrap' }}>
          <button className={`pill ${dataTab === 'results' ? 'active' : ''}`} onClick={() => { setDataTab('results'); setDataPage(1); }} type="button">
            历史
          </button>
          <button className={`pill ${dataTab === 'exports' ? 'active' : ''}`} onClick={() => { setDataTab('exports'); setDataPage(1); }} type="button">
            导出
          </button>
          <button className={`pill ${dataTab === 'shares' ? 'active' : ''}`} onClick={() => { setDataTab('shares'); setDataPage(1); }} type="button">
            分享
          </button>
          <button
            className="btn secondary"
            type="button"
            disabled={dataLoading || !dataItems.length}
            onClick={() => {
              const allSelected = dataItems.length > 0 && dataItems.every((it: any) => dataSelected[(it as any).id]);
              const next: Record<string, boolean> = {};
              for (const it of dataItems as any[]) next[String(it.id)] = !allSelected;
              setDataSelected(next);
            }}
          >
            全选本页
          </button>
          <button
            className="btn secondary"
            type="button"
            disabled={dataLoading || Object.values(dataSelected).filter(Boolean).length === 0}
            onClick={() => {
              const c = Object.values(dataSelected).filter(Boolean).length;
              if (!c) return;
              if (dataTab === 'results') setDataBulkAction('RESULT_DELETE');
              else if (dataTab === 'exports') setDataBulkAction('EXPORT_DELETE');
              else setDataBulkAction('SHARE_REVOKE');
              setDataBulkOpen(true);
            }}
          >
            批量操作（{Object.values(dataSelected).filter(Boolean).length}）
          </button>
          <div className="muted" style={{ marginLeft: 'auto' }}>
            第 {dataPage}/{dataPages} 页
          </div>
          <button className="btn secondary" disabled={dataLoading || dataPage <= 1} onClick={() => setDataPage((p) => Math.max(1, p - 1))} type="button">
            上一页
          </button>
          <button className="btn secondary" disabled={dataLoading || dataPage >= dataPages} onClick={() => setDataPage((p) => Math.min(dataPages, p + 1))} type="button">
            下一页
          </button>
        </div>
        <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
          {dataTab === 'results'
            ? (dataItems as AdminResultItem[]).map((it) => (
                <div key={it.id} style={{ border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14, padding: 12 }}>
                  <div className="row" style={{ justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
                    <div>
                      <div className="row" style={{ gap: 10, alignItems: 'center' }}>
                        <input
                          type="checkbox"
                          checked={!!dataSelected[it.id]}
                          onChange={(e) => setDataSelected((s) => ({ ...s, [it.id]: e.target.checked }))}
                          style={{ width: 16, height: 16 }}
                        />
                        <div style={{ fontWeight: 900 }}>{it.toolName || it.toolId}</div>
                      </div>
                      <div className="muted">{fmt(it.createdAt)}</div>
                    </div>
                    <button
                      className="btn danger"
                      type="button"
                      onClick={async () => {
                        const ok = await confirmDelete({
                          title: '删除历史记录',
                          message: `确认删除该条历史记录？\n${it.id}`,
                          confirmText: '确认删除'
                        });
                        if (!ok) return;
                        setMsg(null);
                        try {
                          await apiFetch(`/api/admin/results/${encodeURIComponent(it.id)}`, { method: 'DELETE' });
                          setMsg('已删除');
                          await loadUserData();
                        } catch {
                          setMsg('删除失败，请稍后重试');
                        }
                      }}
                    >
                      删除
                    </button>
                  </div>
                  <div className="muted" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, marginTop: 8 }}>
                    {(it.renderText || '').slice(0, 240)}
                    {it.renderText && it.renderText.length > 240 ? '…' : ''}
                  </div>
                </div>
              ))
            : dataTab === 'exports'
            ? (dataItems as AdminExportItem[]).map((it) => (
                <div key={it.id} style={{ border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14, padding: 12 }}>
                  <div className="row" style={{ justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
                    <div>
                      <div className="row" style={{ gap: 10, alignItems: 'center' }}>
                        <input
                          type="checkbox"
                          checked={!!dataSelected[it.id]}
                          onChange={(e) => setDataSelected((s) => ({ ...s, [it.id]: e.target.checked }))}
                          style={{ width: 16, height: 16 }}
                        />
                        <div style={{ fontWeight: 900 }}>{it.toolName || it.toolId}</div>
                      </div>
                      <div className="muted">{fmt(it.createdAt)}</div>
                      <div className="muted">Result：{it.resultId}</div>
                    </div>
                    <button
                      className="btn danger"
                      type="button"
                      onClick={async () => {
                        const ok = await confirmDelete({
                          title: '删除导出记录',
                          message: `确认删除该条导出记录？\n${it.id}`,
                          confirmText: '确认删除'
                        });
                        if (!ok) return;
                        setMsg(null);
                        try {
                          await apiFetch(`/api/admin/exports/${encodeURIComponent(it.id)}`, { method: 'DELETE' });
                          setMsg('已删除');
                          await loadUserData();
                        } catch {
                          setMsg('删除失败，请稍后重试');
                        }
                      }}
                    >
                      删除
                    </button>
                  </div>
                </div>
              ))
            : (dataItems as AdminShareItem[]).map((it) => (
                <div key={it.id} style={{ border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14, padding: 12 }}>
                  <div className="row" style={{ justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
                    <div>
                      <div className="row" style={{ gap: 10, alignItems: 'center' }}>
                        <input
                          type="checkbox"
                          checked={!!dataSelected[it.id]}
                          onChange={(e) => setDataSelected((s) => ({ ...s, [it.id]: e.target.checked }))}
                          style={{ width: 16, height: 16 }}
                        />
                        <div style={{ fontWeight: 900 }}>{it.toolId}</div>
                      </div>
                      <div className="muted">创建：{fmt(it.createdAt)}</div>
                      <div className="muted">过期：{fmt(it.expiresAt)}</div>
                      <div className="muted">撤销：{fmt(it.revokedAt)}</div>
                    </div>
                    <div className="row" style={{ gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      <button
                        className="btn secondary"
                        type="button"
                        onClick={async () => {
                          const ok = await confirmRevoke({ title: '撤销分享', message: `确认撤销该分享？\n${it.id}` });
                          if (!ok) return;
                          setMsg(null);
                          try {
                            await apiFetch(`/api/admin/shares/${encodeURIComponent(it.id)}/revoke`, { method: 'POST', body: JSON.stringify({}) });
                            setMsg('已撤销');
                            await loadUserData();
                          } catch {
                            setMsg('撤销失败，请稍后重试');
                          }
                        }}
                      >
                        撤销
                      </button>
                      <button
                        className="btn danger"
                        type="button"
                        onClick={async () => {
                          const ok = await confirmDelete({
                            title: '物理删除分享记录',
                            message: `确认物理删除该分享记录？\n${it.id}\n删除后不可恢复。`,
                            confirmText: '确认删除'
                          });
                          if (!ok) return;
                          setMsg(null);
                          try {
                            await apiFetch(`/api/admin/shares/${encodeURIComponent(it.id)}`, { method: 'DELETE' });
                            setMsg('已删除');
                            await loadUserData();
                          } catch {
                            setMsg('删除失败，请稍后重试');
                          }
                        }}
                      >
                        删除
                      </button>
                    </div>
                  </div>
                </div>
              ))}
          {!dataItems.length && !dataLoading ? <div className="muted">暂无数据</div> : null}
        </div>
      </Modal>

      <Modal open={dataBulkOpen} onClose={() => setDataBulkOpen(false)} title="批量操作">
        <div className="muted" style={{ lineHeight: 1.8 }}>
          已选择 {Object.values(dataSelected).filter(Boolean).length} 条。
        </div>
        <div className="field" style={{ marginTop: 12 }}>
          <div className="muted">操作</div>
          {dataTab === 'shares' ? (
            <select value={dataBulkAction} onChange={(e) => setDataBulkAction(e.target.value as any)}>
              <option value="SHARE_REVOKE">批量撤销分享</option>
              <option value="SHARE_DELETE">批量删除分享记录</option>
            </select>
          ) : (
            <select value={dataBulkAction} onChange={(e) => setDataBulkAction(e.target.value as any)}>
              <option value={dataTab === 'results' ? 'RESULT_DELETE' : 'EXPORT_DELETE'}>批量删除</option>
            </select>
          )}
        </div>
        <div className="field" style={{ marginTop: 12 }}>
          <div className="muted">确认输入：{dataBulkAction === 'SHARE_REVOKE' ? 'CONFIRM' : 'DELETE'}</div>
          <input value={dataBulkConfirm} onChange={(e) => setDataBulkConfirm(e.target.value.slice(0, 20))} placeholder={dataBulkAction === 'SHARE_REVOKE' ? 'CONFIRM' : 'DELETE'} />
        </div>
        <div className="row" style={{ marginTop: 12, justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
          <button
            className={dataBulkAction === 'SHARE_REVOKE' ? 'btn' : 'btn danger'}
            type="button"
            disabled={
              dataBulkRunning ||
              Object.values(dataSelected).filter(Boolean).length === 0 ||
              (dataBulkAction === 'SHARE_REVOKE' ? dataBulkConfirm.trim().toUpperCase() !== 'CONFIRM' : dataBulkConfirm.trim().toUpperCase() !== 'DELETE')
            }
            onClick={async () => {
              const ids = Object.entries(dataSelected)
                .filter(([, v]) => v)
                .map(([k]) => k);
              if (!ids.length) return;
              setDataBulkRunning(true);
              setMsg(null);
              try {
                if (dataBulkAction === 'RESULT_DELETE') {
                  await apiFetch('/api/admin/results/bulk-delete', { method: 'POST', body: JSON.stringify({ ids }) });
                } else if (dataBulkAction === 'EXPORT_DELETE') {
                  await apiFetch('/api/admin/exports/bulk-delete', { method: 'POST', body: JSON.stringify({ ids }) });
                } else if (dataBulkAction === 'SHARE_REVOKE') {
                  await apiFetch('/api/admin/shares/bulk', { method: 'POST', body: JSON.stringify({ action: 'REVOKE', ids }) });
                } else {
                  await apiFetch('/api/admin/shares/bulk', { method: 'POST', body: JSON.stringify({ action: 'DELETE', ids }) });
                }
                setMsg('批量操作完成');
                setDataBulkOpen(false);
                setDataBulkConfirm('');
                setDataSelected({});
                await loadUserData();
              } catch {
                setMsg('批量操作失败，请稍后重试');
              } finally {
                setDataBulkRunning(false);
              }
            }}
          >
            {dataBulkRunning ? '执行中…' : '执行'}
          </button>
          <button className="btn secondary" type="button" onClick={() => setDataBulkOpen(false)}>
            取消
          </button>
        </div>
      </Modal>

      <div className="tool">
        <div className="tool-top">
          <div className="tool-name">管理员操作日志</div>
          <span className="badge">{auditLoading ? '加载中…' : auditTotal ? `${auditTotal} 条` : '0 条'}</span>
        </div>

        <div className="row" style={{ marginTop: 10, gap: 10, flexWrap: 'wrap' }}>
          <input style={{ width: 320 }} value={auditQ} onChange={(e) => setAuditQ(e.target.value.replace(/\s+/g, '').slice(0, 254))} placeholder="按邮箱搜索（操作者/目标）" />
          <select value={auditAction} onChange={(e) => setAuditAction(e.target.value)} style={{ width: 220 }}>
            <option value="">全部动作</option>
            <option value="USER_CREATE">USER_CREATE</option>
            <option value="USER_UPDATE">USER_UPDATE</option>
            <option value="USER_PASSWORD_RESET">USER_PASSWORD_RESET</option>
            <option value="USER_CANCEL">USER_CANCEL</option>
            <option value="USER_RESTORE">USER_RESTORE</option>
            <option value="USER_DELETE">USER_DELETE</option>
            <option value="USER_AI_CONFIG_RESET">USER_AI_CONFIG_RESET</option>
            <option value="USER_COZE_CONFIG_RESET">USER_COZE_CONFIG_RESET</option>
            <option value="LOGIN_UNLOCK">LOGIN_UNLOCK</option>
            <option value="RESULT_DELETE">RESULT_DELETE</option>
            <option value="RESULT_BULK_DELETE">RESULT_BULK_DELETE</option>
            <option value="EXPORT_DELETE">EXPORT_DELETE</option>
            <option value="EXPORT_BULK_DELETE">EXPORT_BULK_DELETE</option>
            <option value="SHARE_REVOKE">SHARE_REVOKE</option>
            <option value="SHARE_BULK_REVOKE">SHARE_BULK_REVOKE</option>
            <option value="SHARE_DELETE">SHARE_DELETE</option>
            <option value="SHARE_BULK_DELETE">SHARE_BULK_DELETE</option>
            <option value="SYSTEM_CONFIG_UPDATE">SYSTEM_CONFIG_UPDATE</option>
            <option value="SITE_ICON_UPDATE">SITE_ICON_UPDATE</option>
            <option value="SITE_ICON_DELETE">SITE_ICON_DELETE</option>
          </select>
          <button
            className="btn secondary"
            type="button"
            disabled={auditLoading}
            onClick={async () => {
              setAuditLoading(true);
              setMsg(null);
              try {
                const qs = new URLSearchParams();
                qs.set('page', String(auditPage));
                qs.set('pageSize', String(auditPageSize));
                if (auditQ.trim()) qs.set('q', auditQ.trim());
                if (auditAction) qs.set('action', auditAction);
                const r = await apiFetch<{ ok: boolean; items: AdminAuditLogItem[]; total: number }>(`/api/admin/audit-logs?${qs.toString()}`, { method: 'GET' });
                setAuditItems(r.items || []);
                setAuditTotal(Number(r.total || 0));
              } catch {
                setAuditItems([]);
                setAuditTotal(0);
                setMsg('加载失败，请稍后重试');
              } finally {
                setAuditLoading(false);
              }
            }}
          >
            查询
          </button>
          <div className="muted" style={{ marginLeft: 'auto' }}>
            第 {auditPage}/{auditPages} 页
          </div>
          <button className="btn secondary" disabled={auditLoading || auditPage <= 1} onClick={() => setAuditPage((p) => Math.max(1, p - 1))} type="button">
            上一页
          </button>
          <button className="btn secondary" disabled={auditLoading || auditPage >= auditPages} onClick={() => setAuditPage((p) => Math.min(auditPages, p + 1))} type="button">
            下一页
          </button>
        </div>

        <div className="muted" style={{ marginTop: 10, lineHeight: 1.8 }}>
          - 日志仅用于排查与追溯，属于高敏感数据
          <br />- 建议生产环境定期归档或清理
        </div>

        <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
          {auditItems.map((it) => (
            <div key={it.id} style={{ border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14, padding: 12 }}>
              <div className="row" style={{ justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontWeight: 900 }}>{it.action}</div>
                  <div className="muted" style={{ lineHeight: 1.7 }}>
                    时间：{fmt(it.createdAt)}
                    <br />
                    操作者：{it.actorEmail || '-'}
                    <br />
                    目标：{it.targetEmail || it.targetUserId || '-'}
                    <br />
                    IP：{it.ip || '-'}
                  </div>
                </div>
                <div className="muted" style={{ maxWidth: 520, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', lineHeight: 1.6 }}>
                  {it.meta != null ? JSON.stringify(it.meta) : ''}
                </div>
              </div>
            </div>
          ))}
          {!auditItems.length && !auditLoading ? <div className="muted">暂无记录</div> : null}
        </div>
      </div>
      <Modal open={mtPreviewOpen} onClose={() => setMtPreviewOpen(false)} title="维护消息预览">
        <div className="std-dialog">
          <div className="std-dialog-message">
            <div dangerouslySetInnerHTML={{ __html: sanitizeAdminHtml(mtEditor) }} />
          </div>
          <div className="dialog-actions">
            <button className="btn secondary" type="button" onClick={() => setMtPreviewOpen(false)}>
              关闭
            </button>
            <button
              className="btn"
              type="button"
              onClick={() => {
                setMtPreviewOpen(false);
                sendMaintenance().catch(() => {});
              }}
            >
              立即发送
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export function SecurityPanel({ email }: { email: string }) {
  const { confirmCancelAccount } = useDialog();
  const toast = useToast();
  const [sending, setSending] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [code, setCode] = useState('');

  const [cancelCode, setCancelCode] = useState('');

  const notify = (kind: 'success' | 'error' | 'info' | 'warning', message: string, dedupeKey: string) => {
    toast.show(kind, message, { dedupeKey, priority: kind === 'error' ? 'high' : kind === 'warning' ? 'normal' : 'low' });
  };

  const sendCode = async () => {
    setSending(true);
    try {
      await apiFetch('/api/auth/send-code', { method: 'POST', body: JSON.stringify({ email }) });
      notify('success', '验证码已发送到邮箱', 'security-send-code-success');
    } catch {
      notify('error', '发送失败，请稍后重试', 'security-send-code-error');
    } finally {
      setSending(false);
    }
  };

  const changePassword = async () => {
    setSubmitting(true);
    try {
      await apiFetch('/api/profile/change-password', { method: 'POST', body: JSON.stringify({ oldPassword, newPassword, code }) });
      notify('success', '修改成功，请重新登录', 'security-change-password-success');
      try {
        window.setTimeout(() => {
          window.location.assign('/login');
        }, 300);
      } catch {
      }
    } catch (e) {
      if (e instanceof ApiError) {
        const c = (e.body as any)?.error;
        if (c === 'CODE_EXPIRED') notify('warning', '验证码已过期，请重新获取', 'security-change-password-code-expired');
        else if (c === 'CODE_INVALID') notify('warning', '验证码不正确', 'security-change-password-code-invalid');
        else if (c === 'INVALID_CREDENTIALS') notify('warning', '原密码不正确', 'security-change-password-invalid-credentials');
        else if (c === 'INVALID_PASSWORD') notify('warning', '新密码不符合规则（8-20 位，仅字母/数字/下划线）', 'security-change-password-invalid-password');
        else notify('error', '修改失败，请稍后重试', 'security-change-password-error');
      } else notify('error', '修改失败', 'security-change-password-error-unknown');
    } finally {
      setSubmitting(false);
    }
  };

  const cancelAccount = async () => {
    setSubmitting(true);
    try {
      const ok = await confirmCancelAccount({
        title: '注销账号',
        message: '确认注销账号？注销后将无法登录，且相关数据将不可恢复',
        confirmText: '确认注销'
      });
      if (!ok) return;
      await apiFetch('/api/profile/cancel', { method: 'POST', body: JSON.stringify({ code: cancelCode }) });
      notify('success', '账号已注销', 'security-cancel-account-success');
      try {
        window.setTimeout(() => {
          window.location.assign('/login');
        }, 300);
      } catch {
      }
    } catch (e) {
      if (e instanceof ApiError) {
        const c = (e.body as any)?.error;
        if (c === 'CODE_EXPIRED') notify('warning', '验证码已过期，请重新获取', 'security-cancel-account-code-expired');
        else if (c === 'CODE_INVALID') notify('warning', '验证码不正确', 'security-cancel-account-code-invalid');
        else notify('error', '注销失败，请稍后重试', 'security-cancel-account-error');
      } else notify('error', '注销失败', 'security-cancel-account-error-unknown');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="section-title">
        <h3>安全</h3>
        <span className="muted">改密码与注销均需要邮箱验证码</span>
      </div>
      <div className="tool" style={{ marginBottom: 12 }}>
        <div className="tool-top">
          <div className="tool-name">修改密码</div>
          <span className="badge">需要验证码</span>
        </div>
        <div className="muted" style={{ lineHeight: 1.8, marginBottom: 10 }}>
          邮箱：{email}
        </div>

        <div className="field">
          <div className="muted">原密码</div>
          <input value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} type="password" autoComplete="current-password" />
        </div>
        <div className="field" style={{ marginTop: 12 }}>
          <div className="muted">新密码（8-20 位，仅字母/数字/下划线）</div>
          <input value={newPassword} onChange={(e) => setNewPassword(e.target.value)} type="password" autoComplete="new-password" />
        </div>
        <div className="field" style={{ marginTop: 12 }}>
          <div className="muted">邮箱验证码</div>
          <input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" />
        </div>
        <div className="row" style={{ marginTop: 12, gap: 10, flexWrap: 'wrap' }}>
          <button className="btn secondary" disabled={sending} onClick={sendCode} type="button">
            {sending ? '发送中…' : '发送验证码'}
          </button>
          <button className="btn" disabled={submitting} onClick={changePassword} type="button">
            {submitting ? '提交中…' : '提交修改'}
          </button>
        </div>
      </div>

      <div className="tool">
        <div className="tool-top">
          <div className="tool-name">注销账号</div>
          <span className="badge">高风险操作</span>
        </div>
        <div className="muted" style={{ lineHeight: 1.8, marginBottom: 10 }}>
          注销后将无法登录，且相关数据可能不可恢复，请谨慎操作。
        </div>
        <div className="field">
          <div className="muted">邮箱验证码</div>
          <input value={cancelCode} onChange={(e) => setCancelCode(e.target.value)} inputMode="numeric" />
        </div>
        <div className="row" style={{ marginTop: 12, gap: 10, flexWrap: 'wrap' }}>
          <button className="btn secondary" disabled={sending} onClick={sendCode} type="button">
            {sending ? '发送中…' : '发送验证码'}
          </button>
          <button className="btn danger" disabled={submitting} onClick={cancelAccount} type="button">
            {submitting ? '提交中…' : '确认注销'}
          </button>
        </div>
      </div>
    </div>
  );
}
